import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Breadcrumb from '../../components/layout/Breadcrumb'
import { DatasetCharacterizationPanel } from '../../components/DatasetCharacterizationPanel'
import RelationshipGraph from './RelationshipGraph'
import { computeTier1 } from '../../lib/sourceDocQA'
import { SUBJECT_META } from '../../data/standards'
import { useAuth } from '../../providers/AuthProvider'
import { supabase } from '../../supabaseClient'
import { useStandards, gradeLabel } from '../../providers/StandardsProvider'
import { useConditions } from '../../hooks/useConditions'
import { useExperiments } from '../../hooks/useExperiments'
import { useKUs } from '../../hooks/useKUs'
import { useKuCrossRefs } from '../../hooks/useKuCrossRefs'
import { usePrompts } from '../../hooks/usePrompts'
import { useRuns } from '../../hooks/useRuns'
import {
  CROSS_REF_RELATIONS,
  Condition,
  CrossRefRelation,
  KU_TYPES,
  KnowledgeUnit,
  PASS_META,
  PassId,
  Prompt,
  PromptSlot,
  Provider,
} from '../../types'

interface KUSummary {
  standard_id: string
  type: string | null
  admin_decision: string | null
}

export default function DatasetDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { findStandard } = useStandards()
  // Single instance — prevents the double-state bug where updates from one
  // hook instance don't reflect in the other's `experiments` array.
  const { experiments, updateExperiment, beginExperiment } = useExperiments(user?.id || '')
  const { prompts, getPrompt } = usePrompts()
  // Cross-ref mappings for the Analysis tab
  const { rows: crossRefs, link: linkKUs, unlink: unlinkKUs, isLinked, targetsOf } = useKuCrossRefs(id)
  const { executeRun } = useRuns(user?.id || '')
  const { fetchKUs } = useKUs()

  const experiment = experiments.find((e) => e.id === id)
  const stdIds = experiment?.standards || []
  const {
    conditions,
    conditionPrompts,
    load: loadConditions,
    createCondition,
    deleteCondition,
    updateCondition,
    setPromptForCondition,
    getPromptsForCondition,
  } = useConditions(experiment?.id)

  useEffect(() => {
    if (experiment?.id) loadConditions()
  }, [experiment?.id, loadConditions])

  const [stdsWithKUs, setStdsWithKUs] = useState<Set<string>>(new Set())
  const [kuSummary, setKUSummary] = useState<KUSummary[]>([])
  const [summaryLoading, setSummaryLoading] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [genError, setGenError] = useState<string | null>(null)
  const [genProgress, setGenProgress] = useState('')
  const [gradeFilter, setGradeFilter] = useState<string | null>(null)
  // Focus mode: one standard at a time for detailed per-pass work
  const [selectedStdId, setSelectedStdId] = useState<string | null>(null)
  const [detailKUs, setDetailKUs] = useState<KnowledgeUnit[]>([])
  const [detailLoading, setDetailLoading] = useState(false)
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null)
  // Analysis tab
  const [analysisStdId, setAnalysisStdId] = useState<string | null>(null)
  const [analysisAllKUs, setAnalysisAllKUs] = useState<KnowledgeUnit[]>([])
  const [pickedKuId, setPickedKuId] = useState<string | null>(null) // first picked KU
  const [pickedPairKuId, setPickedPairKuId] = useState<string | null>(null) // second picked KU for relation selection
  const [analysisView, setAnalysisView] = useState<'matrix' | 'graph' | 'reliability'>('matrix')
  // Dataset tab: which condition(s) to target when running generation.
  // 'all' iterates across every condition (prior behavior). A specific id
  // runs only that condition — useful when the other condition's prompts
  // aren't finalized yet.
  const [runConditionFilter, setRunConditionFilter] = useState<string>('all')
  const [activeTab, setActiveTab] = useState<'standards' | 'conditions' | 'design' | 'analysis'>('standards')
  const [editingCondition, setEditingCondition] = useState<Condition | null>(null)
  const [editName, setEditName] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [deletingCondition, setDeletingCondition] = useState<Condition | null>(null)
  const [conditionsDescription, setConditionsDescription] = useState('')
  const [draftingConditions, setDraftingConditions] = useState(false)
  const [draftConditionsError, setDraftConditionsError] = useState<string | null>(null)
  // Framework & Definitions (prose, paper body) — explicit Save (no autosave)
  const [framework, setFramework] = useState('')
  const [savedFramework, setSavedFramework] = useState('')
  const [savingFramework, setSavingFramework] = useState(false)
  const [draftingFramework, setDraftingFramework] = useState(false)
  const [improvingFramework, setImprovingFramework] = useState(false)
  const [frameworkError, setFrameworkError] = useState<string | null>(null)
  const [reviewingFramework, setReviewingFramework] = useState(false)
  const [frameworkReview, setFrameworkReview] = useState<{
    overall?: string
    strengths?: string[]
    gaps?: string[]
    ambiguities?: string[]
    suggestions?: string[]
  } | null>(null)

  // System Prompt (AI-optimized, paper appendix + LLM input) — explicit Save
  const [optimizedPrompt, setOptimizedPrompt] = useState('')
  const [savedSystemPrompt, setSavedSystemPrompt] = useState('')
  const [savingSystemPrompt, setSavingSystemPrompt] = useState(false)
  const [optimizing, setOptimizing] = useState(false)
  const [preOptimizeSnapshot, setPreOptimizeSnapshot] = useState<string | null>(null)
  const [optimizedError, setOptimizedError] = useState<string | null>(null)
  const [reviewingOptimized, setReviewingOptimized] = useState(false)
  const [improvingOptimized, setImprovingOptimized] = useState(false)
  const [optimizedReview, setOptimizedReview] = useState<{
    overall?: string
    strengths?: string[]
    gaps?: string[]
    ambiguities?: string[]
    suggestions?: string[]
  } | null>(null)
  const [expName, setExpName] = useState('')
  const [savedExpName, setSavedExpName] = useState('')
  const [researchQ, setResearchQ] = useState('')
  const [savedResearchQ, setSavedResearchQ] = useState('')
  const [savedDescription, setSavedDescription] = useState('')
  const [savedConditionsDescription, setSavedConditionsDescription] = useState('')
  const [savingMetadata, setSavingMetadata] = useState(false)
  const [metadataError, setMetadataError] = useState<string | null>(null)
  const [, setHypothesis] = useState('')
  const [, setMethodology] = useState('')
  const [description, setDescription] = useState('')

  const [drafting, setDrafting] = useState(false)
  const [draftError, setDraftError] = useState<string | null>(null)
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')

  // Begin Experiment confirmation flow
  const [beginOpen, setBeginOpen] = useState(false)
  const [beginRunning, setBeginRunning] = useState(false)
  const [beginError, setBeginError] = useState<string | null>(null)
  // Typed confirmation — user must retype the experiment's name to unlock
  // the destructive Begin button. Adds a real friction step so a misclick
  // can't trigger KU deletion.
  const [beginConfirmName, setBeginConfirmName] = useState('')

  // ── localStorage cache key for this experiment's draft fields ──
  // Every field edit is mirrored here synchronously so data survives tab
  // close, refresh, or navigation — even if the debounced DB save hasn't
  // fired yet. Restored on next mount if newer than the DB value.
  const cacheKey = `kosmos_exp_draft_${experiment?.id || 'none'}`

  // Sync all editable fields when experiment loads. Prefers localStorage
  // cache if it's newer than the DB (recovery from interrupted save).
  useEffect(() => {
    if (!experiment) return
    let cached: Record<string, { v: string; at: number }> = {}
    try {
      const raw = localStorage.getItem(cacheKey)
      if (raw) cached = JSON.parse(raw)
    } catch {}
    const dbTime = new Date(experiment.updated_at || 0).getTime()
    console.log('[sync] experiment loaded', {
      id: experiment.id,
      updated_at: experiment.updated_at,
      framework_definitions_length: (experiment.framework_definitions || '').length,
      framework_definitions_preview: (experiment.framework_definitions || '').slice(0, 50),
      system_prompt_length: (experiment.system_prompt || '').length,
      cacheKey,
      cachedKeys: Object.keys(cached),
    })
    const pick = (field: string, dbVal: string | null) => {
      const c = cached[field]
      // Use cache only if it's newer than the DB AND it has non-empty content.
      if (c && c.at > dbTime && typeof c.v === 'string' && c.v.length > 0) {
        console.log(`[sync] pick ${field}: cache wins`, { cacheLen: c.v.length, dbLen: (dbVal || '').length })
        return c.v
      }
      console.log(`[sync] pick ${field}: db wins`, { dbLen: (dbVal || '').length })
      return dbVal || ''
    }
    const n = pick('name', experiment.name)
    const rq = pick('research_question', experiment.research_question)
    const desc = pick('dataset_description', experiment.dataset_description)
    const condDesc = pick('conditions_description', experiment.conditions_description)
    setExpName(n)
    setResearchQ(rq)
    setHypothesis(experiment.hypothesis || '')
    setMethodology(experiment.methodology_notes || '')
    setDescription(desc)
    setConditionsDescription(condDesc)
    // Track last-persisted values so every section can detect dirty state.
    setSavedExpName(experiment.name || '')
    setSavedResearchQ(experiment.research_question || '')
    setSavedDescription(experiment.dataset_description || '')
    setSavedConditionsDescription(experiment.conditions_description || '')
    const fw = pick('framework_definitions', experiment.framework_definitions)
    const sp = pick('system_prompt', experiment.system_prompt)
    setFramework(fw)
    setOptimizedPrompt(sp)
    // Track the last-persisted version so the dirty-flag & Save button
    // reflect whether the editor differs from what's in the DB.
    setSavedFramework(experiment.framework_definitions || '')
    setSavedSystemPrompt(experiment.system_prompt || '')
  }, [experiment?.id]) // eslint-disable-line

  // Mirror every field change to localStorage immediately (synchronous,
  // cannot be lost to unmount or network drop). Field-level timestamps
  // let the restore logic pick the freshest value per field on remount.
  const writeCache = useCallback(
    (field: string, v: string) => {
      if (!experiment?.id) return
      try {
        const raw = localStorage.getItem(cacheKey)
        const cached = raw ? JSON.parse(raw) : {}
        if (v && v.length > 0) {
          // Only cache non-empty drafts. An empty value has no information
          // content — leaving it in the cache can clobber newer DB values
          // on next mount (since the cache's timestamp is always "now",
          // which beats the saved-at timestamp of the DB).
          cached[field] = { v, at: Date.now() }
        } else {
          delete cached[field]
        }
        localStorage.setItem(cacheKey, JSON.stringify(cached))
      } catch {}
    },
    [cacheKey, experiment?.id],
  )
  useEffect(() => writeCache('name', expName), [expName, writeCache])
  useEffect(() => writeCache('research_question', researchQ), [researchQ, writeCache])
  useEffect(() => writeCache('dataset_description', description), [description, writeCache])
  useEffect(() => writeCache('conditions_description', conditionsDescription), [conditionsDescription, writeCache])
  useEffect(() => writeCache('framework_definitions', framework), [framework, writeCache])
  useEffect(() => writeCache('system_prompt', optimizedPrompt), [optimizedPrompt, writeCache])

  // All DB writes are explicit now — Save buttons per section commit.
  // localStorage mirror above still runs on every keystroke so drafts
  // survive refresh/close even before the user clicks Save.

  const loadSummary = useCallback(async () => {
    if (!stdIds.length) return
    setSummaryLoading(true)
    const { data } = await supabase
      .from('knowledge_units')
      .select('standard_id, type, admin_decision')
      .in('standard_id', stdIds)
    const rows = (data as KUSummary[]) || []
    setKUSummary(rows)
    setStdsWithKUs(new Set(rows.map((r) => r.standard_id)))
    setSummaryLoading(false)
  }, [stdIds.join(',')]) // eslint-disable-line

  useEffect(() => {
    loadSummary()
  }, [loadSummary])

  // Resolve prompts for a pass/slot: use condition-specific assignment if present,
  // otherwise fall back to the globally-active prompt.
  const resolvePrompt = (passId: PassId, slot: PromptSlot, conditionId?: string): Prompt | undefined => {
    if (conditionId) {
      const cp = conditionPrompts.find((x) => x.condition_id === conditionId && x.pass_id === passId && x.slot === slot)
      if (cp) {
        const assigned = prompts.find((p) => p.id === cp.prompt_id)
        if (assigned) return assigned
      }
      // No assignment for this condition/pass/slot — fall through to active
    }
    return getPrompt(passId, slot)
  }

  // Run one pass for one standard under one condition (or no condition)
  const runPassForStandard = async (stdId: string, passId: PassId, conditionId: string | null) => {
    const stdMeta = findStandard(stdId)
    if (!stdMeta) return
    const pass = PASS_META.find((p) => p.id === passId)!

    let sourceDoc: string | undefined
    if (pass.hasGenerate) {
      const genPrompt = resolvePrompt(passId, 'generate', conditionId || undefined)
      if (!genPrompt) return
      const genResult = await executeRun({
        standardId: stdId,
        standardText: stdMeta.std.text,
        subject: stdMeta.subject,
        grade: stdMeta.grade,
        passId,
        provider: 'anthropic' as Provider,
        model: 'claude-sonnet-4-6',
        promptText: genPrompt.text,
        slot: 'generate',
        promptLabel: genPrompt.label,
        kuCount: 30,
        experimentId: experiment?.id,
        conditionId: conditionId || undefined,
        // System prompt is the extraction layer — omit on generate runs so
        // the extraction invariants don't warp source-doc production.
      })
      sourceDoc = genResult?.run.source_doc || undefined
      // Fire off Tier 2 QA rubric scoring in the background. The Edge
      // Function persists the result onto the run row, so no await is
      // needed here — later UI reads pick it up on refresh.
      if (sourceDoc && genResult?.run.id) {
        const expectedStyle = passId === 'pass2' ? 'pedagogy' : 'article'
        supabase.functions
          .invoke('extract-kos', {
            body: {
              slot: 'qa_rubric',
              runId: genResult.run.id,
              standardText: stdMeta.std.text,
              subject: stdMeta.subject,
              grade: stdMeta.grade,
              sourceDoc,
              expectedStyle,
              provider: 'anthropic',
              model: 'claude-sonnet-4-6',
            },
          })
          .catch((err: unknown) => console.error('qa_rubric call failed:', err))
      }
    }
    const extractPrompt = resolvePrompt(passId, 'extract', conditionId || undefined)
    if (!extractPrompt) return
    await executeRun({
      standardId: stdId,
      standardText: stdMeta.std.text,
      subject: stdMeta.subject,
      grade: stdMeta.grade,
      passId,
      provider: 'anthropic' as Provider,
      model: 'claude-sonnet-4-6',
      promptText: extractPrompt.text,
      slot: 'extract',
      promptLabel: extractPrompt.label,
      sourceDoc,
      kuCount: 5,
      experimentId: experiment?.id,
      conditionId: conditionId || undefined,
      systemPrompt: experiment?.system_prompt || experiment?.framework_definitions || undefined,
    })
  }

  // Generate for a specific pass (or all passes when passFilter is null).
  // If conditions exist, runs once per condition. Otherwise runs with the active prompt.
  // ── Begin Experiment: pre-flight + confirm + RPC ─────────────────────────
  const beginPreflight = (): string[] => {
    const issues: string[] = []
    if (!experiment) return ['Experiment not loaded']
    if (experiment.phase !== 'design' || experiment.started_at) {
      issues.push('Already started')
      return issues
    }
    if (!stdIds.length) issues.push('No standards selected')
    if (!conditions.length) issues.push('No conditions defined')
    return issues
  }

  const handleBegin = async () => {
    if (!experiment) return
    setBeginRunning(true)
    setBeginError(null)
    const result = await beginExperiment(experiment.id)
    if (!result.ok) {
      setBeginError(result.error || 'Failed to begin')
      setBeginRunning(false)
      return
    }
    await loadSummary()
    setBeginRunning(false)
    setBeginOpen(false)
    setBeginConfirmName('')
  }

  const handleGenerate = async (passFilter: PassId | null) => {
    if (!user) return
    setGenerating(true)
    setGenError(null)

    const targetPasses = passFilter ? PASS_META.filter((p) => p.id === passFilter) : PASS_META
    const allIds = conditions.map((c) => c.id)
    const filteredIds = runConditionFilter === 'all' ? allIds : allIds.filter((id) => id === runConditionFilter)
    const targetConditions: (string | null)[] = conditions.length > 0 ? filteredIds : [null]

    // Build the work list: standard × pass × condition
    const work: { stdId: string; passId: PassId; conditionId: string | null; conditionName: string }[] = []
    for (const stdId of stdIds) {
      const stdKUs = kuSummary.filter((k) => k.standard_id === stdId)
      const hasAnyKUs = stdKUs.length > 0
      // Only add if this standard doesn't already have KUs (coarse check, matches prior behavior)
      if (hasAnyKUs) continue
      for (const pass of targetPasses) {
        for (const condId of targetConditions) {
          const condName = condId ? conditions.find((c) => c.id === condId)?.name || '' : ''
          work.push({ stdId, passId: pass.id, conditionId: condId, conditionName: condName })
        }
      }
    }

    let done = 0
    for (const { stdId, passId, conditionId, conditionName } of work) {
      const pass = PASS_META.find((p) => p.id === passId)!
      const tag = conditionName ? ` [${conditionName}]` : ''
      setGenProgress(`Generating ${pass.label}${tag} for ${stdId}… (${done + 1}/${work.length})`)
      try {
        await runPassForStandard(stdId, passId, conditionId)
      } catch {}
      done++
    }

    await loadSummary()
    setGenerating(false)
    setGenProgress('')
  }

  const handleGenerateAll = () => handleGenerate(null)

  // Load KU + run records for the selected standard (for focus panel).
  const loadDetailKUs = useCallback(async () => {
    if (!selectedStdId) {
      setDetailKUs([])
      return
    }
    setDetailLoading(true)
    const rows = await fetchKUs(selectedStdId)
    setDetailKUs(rows)
    setDetailLoading(false)
  }, [selectedStdId, fetchKUs])

  useEffect(() => {
    loadDetailKUs()
  }, [loadDetailKUs])

  // Focus-mode: generate ONE pass for the selected standard, across the
  // currently-filtered conditions (respects runConditionFilter).
  const handleGenerateStandardPass = async (stdId: string, passId: PassId) => {
    if (!user) return
    setGenerating(true)
    setGenError(null)
    const allIds = conditions.map((c) => c.id)
    const filteredIds = runConditionFilter === 'all' ? allIds : allIds.filter((id) => id === runConditionFilter)
    const targetConditions: (string | null)[] = conditions.length > 0 ? filteredIds : [null]
    const pass = PASS_META.find((p) => p.id === passId)!
    let done = 0
    for (const condId of targetConditions) {
      const condName = condId ? conditions.find((c) => c.id === condId)?.name || '' : ''
      const tag = condName ? ` [${condName}]` : ''
      setGenProgress(`Generating ${pass.label}${tag} for ${stdId}… (${done + 1}/${targetConditions.length})`)
      try {
        await runPassForStandard(stdId, passId, condId)
      } catch {}
      done++
    }
    await loadSummary()
    await loadDetailKUs()
    setGenerating(false)
    setGenProgress('')
  }

  // ── Load all KUs for the Analysis tab ─────────────────────────
  // Kept separate from kuSummary (which is slim for the Dataset progress
  // dots) — analysis needs name/description/pass_id to build the matrix.
  const loadAnalysisKUs = useCallback(async () => {
    if (!experiment?.id) return
    const { data } = await supabase
      .from('knowledge_units')
      .select('id, standard_id, pass_id, name, description, proposed_type, type, condition_id')
      .eq('experiment_id', experiment.id)
    setAnalysisAllKUs((data as KnowledgeUnit[]) || [])
  }, [experiment?.id])

  useEffect(() => {
    if (activeTab === 'analysis') loadAnalysisKUs()
  }, [activeTab, loadAnalysisKUs])

  // ── Delete a single run + its KUs (for cleanup of bad/exploratory runs) ──
  const handleDeleteRun = async (runId: string) => {
    if (!window.confirm('Delete this run and all KUs it produced? This cannot be undone.')) return
    // Delete KUs first (explicit — FK does not cascade by default)
    const { error: kuErr } = await supabase.from('knowledge_units').delete().eq('run_id', runId)
    if (kuErr) {
      setGenError(`Failed to delete KUs: ${kuErr.message}`)
      return
    }
    const { error: runErr } = await supabase.from('runs').delete().eq('id', runId)
    if (runErr) {
      setGenError(`Failed to delete run: ${runErr.message}`)
      return
    }
    await loadSummary()
    await loadDetailKUs()
    setExpandedRunId(null)
  }

  // ── Condition handlers ─────────────────────────────────────────
  const openNewCondition = () => {
    setEditingCondition({
      id: '',
      experiment_id: experiment?.id || '',
      name: '',
      description: '',
      sort_order: 0,
      phase: 'drafting',
      locked_prompts: null,
      started_at: null,
      locked_at: null,
      created_at: '',
    })
    setEditName('')
    setEditDescription('')
  }

  const openEditCondition = (c: Condition) => {
    setEditingCondition(c)
    setEditName(c.name)
    setEditDescription(c.description || '')
  }

  const saveCondition = async () => {
    if (!editingCondition) return
    if (editingCondition.id) {
      await updateCondition(editingCondition.id, { name: editName.trim(), description: editDescription.trim() || null })
    } else {
      await createCondition(editName.trim(), editDescription.trim() || undefined)
    }
    setEditingCondition(null)
  }

  const confirmDeleteCondition = async () => {
    if (!deletingCondition) return
    await deleteCondition(deletingCondition.id)
    setDeletingCondition(null)
  }

  const handlePromptChange = async (conditionId: string, passId: PassId, slot: PromptSlot, promptId: string) => {
    await setPromptForCondition(conditionId, passId, slot, promptId)
  }

  // Get the prompt ID for a condition + pass + slot, or empty string if none assigned
  const getAssignedPromptId = (conditionId: string, passId: PassId, slot: PromptSlot): string => {
    const cp = conditionPrompts.find((x) => x.condition_id === conditionId && x.pass_id === passId && x.slot === slot)
    return cp?.prompt_id || ''
  }

  // Filter prompts by pass + slot for the dropdown options
  const getPromptOptions = (passId: PassId, slot: PromptSlot): Prompt[] => {
    return prompts.filter((p) => p.pass_id === passId && p.slot === slot)
  }

  const handleDraftDescription = async () => {
    if (!experiment) return
    setDrafting(true)
    setDraftError(null)
    try {
      // Build domain distribution from standards metadata
      const domainCount: Record<string, number> = {}
      for (const stdId of stdIds) {
        const m = findStandard(stdId)
        if (m?.domain) domainCount[m.domain] = (domainCount[m.domain] || 0) + 1
      }
      const domains = Object.entries(domainCount)
        .sort((a, b) => b[1] - a[1])
        .map(([name, count]) => ({ name, count }))

      // Sample standard texts (up to 15)
      const sampleStandards: string[] = []
      for (const stdId of stdIds) {
        if (sampleStandards.length >= 15) break
        const m = findStandard(stdId)
        if (m?.std.text) sampleStandards.push(m.std.text)
      }

      const { data, error } = await supabase.functions.invoke('extract-kos', {
        body: {
          slot: 'describe',
          provider: 'anthropic',
          model: 'claude-sonnet-4-6',
          subject: experiment.subject,
          grades: experiment.grades,
          standardCount: stdIds.length,
          domains,
          sampleStandards,
          research_question: researchQ,
          systemPrompt: experiment.system_prompt || undefined,
        },
      })

      if (error) {
        let msg = error.message
        try {
          // eslint-disable-next-line
          const errBody = await (error as any).context?.text?.()
          if (errBody) {
            const parsed = JSON.parse(errBody)
            msg = parsed.error || parsed.message || errBody
          }
        } catch {}
        throw new Error(msg)
      }

      if (data?.description) {
        setDescription(data.description)
        await updateExperiment(experiment.id, { dataset_description: data.description })
        setSavedDescription(data.description)
      }
    } catch (err: unknown) {
      setDraftError(err instanceof Error ? err.message : 'Draft failed')
    } finally {
      setDrafting(false)
    }
  }

  // ── AI draft: experimental conditions description ──────────────
  const handleDraftConditions = async () => {
    if (!experiment) return
    if (conditions.length === 0) {
      setDraftConditionsError('Define at least one condition first')
      return
    }
    setDraftingConditions(true)
    setDraftConditionsError(null)

    try {
      // Shape conditions + their prompts for the prompt builder
      const condPayload = conditions.map((c) => ({
        name: c.name,
        description: c.description,
        prompts: getPromptsForCondition(c.id)
          .map((cp) => {
            const p = prompts.find((x) => x.id === cp.prompt_id)
            return p
              ? {
                  passId: cp.pass_id,
                  slot: cp.slot,
                  label: p.label,
                  roleDescription: p.role_description || undefined,
                }
              : null
          })
          .filter((x): x is NonNullable<typeof x> => x !== null),
      }))

      const { data, error } = await supabase.functions.invoke('extract-kos', {
        body: {
          slot: 'describe_conditions',
          provider: 'anthropic',
          model: 'claude-sonnet-4-6',
          subject: experiment.subject,
          standardCount: stdIds.length,
          researchQuestion: researchQ,
          hypothesis: experiment.hypothesis || undefined,
          conditions: condPayload,
          systemPrompt: experiment.system_prompt || undefined,
        },
      })

      if (error) {
        let msg = error.message
        try {
          // eslint-disable-next-line
          const errBody = await (error as any).context?.text?.()
          if (errBody) {
            const parsed = JSON.parse(errBody)
            msg = parsed.error || parsed.message || errBody
          }
        } catch {}
        throw new Error(msg)
      }

      if (data?.description) {
        setConditionsDescription(data.description)
        await updateExperiment(experiment.id, { conditions_description: data.description })
        setSavedConditionsDescription(data.description)
      }
    } catch (err: unknown) {
      setDraftConditionsError(err instanceof Error ? err.message : 'Draft failed')
    } finally {
      setDraftingConditions(false)
    }
  }

  // ── AI draft: starter Framework & Definitions (prose, paper body) ──
  const handleDraftFramework = async () => {
    if (!experiment) return
    setDraftingFramework(true)
    setFrameworkError(null)
    try {
      const metaPrompt = `You are helping a researcher draft the "Framework & Definitions" section of their research paper. This text will appear in the paper body — it is a prose explanation of the theoretical commitments underlying the experiment.

Research question: "${researchQ || 'Not specified'}"
Subject: ${experiment.subject}
Grade(s): ${(experiment.grades || []).join(', ') || 'Not specified'}

Draft a theory-neutral starter. Include:
- A concise definition of "Knowledge Unit (KU)" in the context of this research
- Any scope constraints implied by the research question
- A placeholder note saying "Add theory-specific definitions (CDT, CoL, etc.) here" — do NOT presuppose any framework

Write in academic prose suitable for a paper body (not a system prompt). 3–5 short paragraphs. Return only the section text, no preamble or commentary.`

      const { data, error } = await supabase.functions.invoke('extract-kos', {
        body: {
          slot: 'generate',
          passId: 'pass1',
          provider: 'anthropic',
          model: 'claude-sonnet-4-6',
          subject: experiment.subject,
          promptText: metaPrompt,
          pilot: true,
        },
      })
      if (error) throw new Error(error.message)
      const text: string = data?.sourceDoc || data?.description || ''
      if (text) {
        const next = text.trim()
        setFramework(next)
        await updateExperiment(experiment.id, { framework_definitions: next })
        setSavedFramework(next)
      }
    } catch (err: unknown) {
      setFrameworkError(err instanceof Error ? err.message : 'Draft failed')
    } finally {
      setDraftingFramework(false)
    }
  }

  // ── AI improve: revise framework phrasing, preserve intent ──
  const handleImproveFramework = async () => {
    if (!experiment || !framework.trim()) return
    setImprovingFramework(true)
    setFrameworkError(null)
    try {
      const { data, error } = await supabase.functions.invoke('extract-kos', {
        body: {
          slot: 'improve_system',
          provider: 'anthropic',
          model: 'claude-sonnet-4-6',
          subject: experiment.subject,
          systemPrompt: framework,
          researchQuestion: researchQ,
        },
      })
      if (error) throw new Error(error.message)
      if (data?.description) {
        setFramework(data.description)
        await updateExperiment(experiment.id, { framework_definitions: data.description })
        setSavedFramework(data.description)
      }
    } catch (err: unknown) {
      setFrameworkError(err instanceof Error ? err.message : 'Improve failed')
    } finally {
      setImprovingFramework(false)
    }
  }

  // ── AI review: diagnostic on the framework ──
  const handleReviewFramework = async () => {
    if (!experiment || !framework.trim()) return
    setReviewingFramework(true)
    setFrameworkError(null)
    setFrameworkReview(null)
    try {
      const { data, error } = await supabase.functions.invoke('extract-kos', {
        body: {
          slot: 'review_system',
          provider: 'anthropic',
          model: 'claude-sonnet-4-6',
          subject: experiment.subject,
          systemPrompt: framework,
          researchQuestion: researchQ,
        },
      })
      if (error) throw new Error(error.message)
      if (data?.review) setFrameworkReview(data.review)
    } catch (err: unknown) {
      setFrameworkError(err instanceof Error ? err.message : 'Review failed')
    } finally {
      setReviewingFramework(false)
    }
  }

  // ── AI optimize: translate Framework → machine-optimized System Prompt.
  // Writes to the separate optimizedPrompt field (NOT the framework itself).
  // TODO(future): upgrade to a proper diff view for line-by-line review.
  const handleOptimizeFramework = async () => {
    if (!experiment || !framework.trim()) return
    setOptimizing(true)
    setOptimizedError(null)
    try {
      const { data, error } = await supabase.functions.invoke('extract-kos', {
        body: {
          slot: 'optimize_system',
          provider: 'anthropic',
          model: 'claude-sonnet-4-6',
          subject: experiment.subject,
          systemPrompt: framework,
          researchQuestion: researchQ,
        },
      })
      if (error) throw new Error(error.message)
      if (data?.description) {
        // Snapshot the current optimized version so researcher can undo
        setPreOptimizeSnapshot(optimizedPrompt)
        setOptimizedPrompt(data.description)
        await updateExperiment(experiment.id, { system_prompt: data.description })
        setSavedSystemPrompt(data.description)
      }
    } catch (err: unknown) {
      setOptimizedError(err instanceof Error ? err.message : 'Optimize failed')
    } finally {
      setOptimizing(false)
    }
  }

  const undoOptimize = () => {
    if (preOptimizeSnapshot === null) return
    setOptimizedPrompt(preOptimizeSnapshot)
    setPreOptimizeSnapshot(null)
    updateExperiment(experiment?.id || '', { system_prompt: preOptimizeSnapshot.trim() || null })
    setSavedSystemPrompt(preOptimizeSnapshot)
  }

  // ── Review + Improve for the Optimized System Prompt directly ──
  const handleReviewOptimized = async () => {
    if (!experiment || !optimizedPrompt.trim()) return
    setReviewingOptimized(true)
    setOptimizedError(null)
    setOptimizedReview(null)
    try {
      const { data, error } = await supabase.functions.invoke('extract-kos', {
        body: {
          slot: 'review_system',
          provider: 'anthropic',
          model: 'claude-sonnet-4-6',
          subject: experiment.subject,
          systemPrompt: optimizedPrompt,
          researchQuestion: researchQ,
        },
      })
      if (error) throw new Error(error.message)
      if (data?.review) setOptimizedReview(data.review)
    } catch (err: unknown) {
      setOptimizedError(err instanceof Error ? err.message : 'Review failed')
    } finally {
      setReviewingOptimized(false)
    }
  }

  const handleImproveOptimized = async () => {
    if (!experiment || !optimizedPrompt.trim()) return
    setImprovingOptimized(true)
    setOptimizedError(null)
    try {
      const { data, error } = await supabase.functions.invoke('extract-kos', {
        body: {
          slot: 'improve_system',
          provider: 'anthropic',
          model: 'claude-sonnet-4-6',
          subject: experiment.subject,
          systemPrompt: optimizedPrompt,
          researchQuestion: researchQ,
        },
      })
      if (error) throw new Error(error.message)
      if (data?.description) {
        // Snapshot so researcher can undo
        setPreOptimizeSnapshot(optimizedPrompt)
        setOptimizedPrompt(data.description)
        await updateExperiment(experiment.id, { system_prompt: data.description })
        setSavedSystemPrompt(data.description)
      }
    } catch (err: unknown) {
      setOptimizedError(err instanceof Error ? err.message : 'Improve failed')
    } finally {
      setImprovingOptimized(false)
    }
  }

  // ── Dirty-state flags per field ───────────────────────────────
  const expNameDirty = expName !== savedExpName
  const researchQDirty = researchQ !== savedResearchQ
  const descriptionDirty = description !== savedDescription
  const conditionsDescriptionDirty = conditionsDescription !== savedConditionsDescription
  const metadataDirty = expNameDirty || researchQDirty || descriptionDirty || conditionsDescriptionDirty
  const metadataDirtyCount =
    Number(expNameDirty) + Number(researchQDirty) + Number(descriptionDirty) + Number(conditionsDescriptionDirty)
  const frameworkDirty = framework !== savedFramework
  const systemPromptDirty = optimizedPrompt !== savedSystemPrompt

  // Single-shot "Save Changes" for the non-Framework/SystemPrompt fields.
  // Framework and System Prompt keep their dedicated section Save buttons
  // because AI actions live in those regions and per-section commits are
  // more natural there.
  const handleSaveMetadata = async () => {
    if (!experiment) return
    setSavingMetadata(true)
    setMetadataError(null)
    try {
      await updateExperiment(experiment.id, {
        name: expName.trim() || experiment.name,
        research_question: researchQ.trim() || null,
        dataset_description: description.trim() || null,
        conditions_description: conditionsDescription.trim() || null,
      })
      setSavedExpName(expName)
      setSavedResearchQ(researchQ)
      setSavedDescription(description)
      setSavedConditionsDescription(conditionsDescription)
    } catch (err: unknown) {
      setMetadataError(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setSavingMetadata(false)
    }
  }

  const handleSaveFramework = async () => {
    if (!experiment) return
    const payload = { framework_definitions: framework.trim() || null }
    console.log('[save] Framework click', {
      frameworkStateLen: framework.length,
      framework_preview: framework.slice(0, 60),
      payload_length: (payload.framework_definitions || '').length,
      payload_preview: (payload.framework_definitions || '').slice(0, 60),
    })
    setSavingFramework(true)
    setFrameworkError(null)
    try {
      await updateExperiment(experiment.id, payload)
      setSavedFramework(framework)
      console.log('[save] Framework: committed to DB, sent length =', (payload.framework_definitions || '').length)
    } catch (err: unknown) {
      console.error('[save] Framework: failed', err)
      setFrameworkError(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setSavingFramework(false)
    }
  }

  const handleSaveSystemPrompt = async () => {
    if (!experiment) return
    setSavingSystemPrompt(true)
    setOptimizedError(null)
    try {
      await updateExperiment(experiment.id, { system_prompt: optimizedPrompt.trim() || null })
      setSavedSystemPrompt(optimizedPrompt)
    } catch (err: unknown) {
      setOptimizedError(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setSavingSystemPrompt(false)
    }
  }

  if (!experiment) return null

  const sm = SUBJECT_META[experiment.subject]
  const grades = Array.isArray(experiment.grades) ? experiment.grades : []

  const acceptedKUs = kuSummary.filter((k) => k.admin_decision === 'accepted' && k.type)
  const typeDist = KU_TYPES.reduce((acc, t) => ({ ...acc, [t.value]: 0 }), {} as Record<string, number>)
  acceptedKUs.forEach((k) => {
    if (k.type && typeDist[k.type] !== undefined) typeDist[k.type]++
  })

  // ── Workflow step completion states ─────────────────────────────
  // Each step surfaces a simple ready/done signal so the stepper header
  // can guide the researcher to their next actionable task.
  const frameworkDone = !!(framework.trim() || optimizedPrompt.trim())
  const conditionsDone = conditions.length > 0 && conditions.every((c) => getPromptsForCondition(c.id).length > 0)
  const kuCount = kuSummary.length
  const datasetDone = kuCount > 0
  const reviewCount = kuSummary.filter((k) => k.admin_decision && k.admin_decision !== 'inReview').length

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'background.default' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Breadcrumb label="Experiments" onBack={() => navigate('/datasets')} />
        {/* Save status indicator */}
        {saveStatus !== 'idle' && (
          <Typography
            sx={{
              fontSize: 10.5,
              fontWeight: 700,
              mr: 2,
              color:
                saveStatus === 'saving'
                  ? 'text.secondary'
                  : saveStatus === 'saved'
                  ? '#16a34a'
                  : saveStatus === 'error'
                  ? '#dc2626'
                  : 'text.disabled',
            }}
          >
            {saveStatus === 'saving' && '○ Saving…'}
            {saveStatus === 'saved' && '✓ Saved'}
            {saveStatus === 'error' && '⚠ Save failed'}
          </Typography>
        )}
      </Box>

      {/* ── Header (always visible) ── */}
      <Box
        sx={{
          px: 2.5,
          pt: 2,
          pb: 2,
          flexShrink: 0,
          bgcolor: 'background.paper',
          borderBottom: `3px solid ${sm.color}`,
          position: 'relative',
        }}
      >
        {/* Save Changes + Begin Experiment / Started status (top-right) */}
        <Box
          sx={{ position: 'absolute', top: 12, right: 12, zIndex: 1, display: 'flex', gap: 1, alignItems: 'center' }}
        >
          <Button
            variant={metadataDirty ? 'contained' : 'outlined'}
            size="small"
            onClick={handleSaveMetadata}
            disabled={!metadataDirty || savingMetadata}
            title={
              metadataDirty
                ? `Save ${metadataDirtyCount} unsaved field${
                    metadataDirtyCount !== 1 ? 's' : ''
                  } (name, research question, dataset/conditions descriptions)`
                : 'No unsaved changes in experiment metadata'
            }
            sx={{
              fontSize: 12,
              fontWeight: 800,
              ...(metadataDirty ? { bgcolor: '#16a34a', color: 'white', '&:hover': { bgcolor: '#15803d' } } : {}),
            }}
          >
            {savingMetadata ? <CircularProgress size={14} sx={{ mr: 0.75, color: 'inherit' }} /> : null}
            {savingMetadata ? 'Saving…' : metadataDirty ? `💾 Save (${metadataDirtyCount})` : '✓ Saved'}
          </Button>
          {experiment.started_at ? (
            <Chip
              label={`✓ Started ${new Date(experiment.started_at).toLocaleDateString()}`}
              size="small"
              sx={{
                bgcolor: '#dcfce7',
                color: '#166534',
                fontWeight: 700,
                fontSize: 11,
                border: '1px solid #bbf7d0',
              }}
            />
          ) : (
            <Button
              variant="contained"
              size="small"
              onClick={() => setBeginOpen(true)}
              disabled={beginPreflight().length > 0}
              title={beginPreflight().join(' · ') || 'Lock framework + standards and begin canonical collection'}
              sx={{
                fontSize: 12,
                fontWeight: 800,
                bgcolor: '#16a34a',
                color: 'white',
                '&:hover': { bgcolor: '#15803d' },
              }}
            >
              ▶ Begin Experiment
            </Button>
          )}
        </Box>
        {metadataError && (
          <Alert severity="error" sx={{ mb: 1, fontSize: 12, mt: 4 }} onClose={() => setMetadataError(null)}>
            {metadataError}
          </Alert>
        )}
        <Typography
          sx={{
            fontWeight: 700,
            color: 'text.secondary',
            fontSize: 10.5,
            letterSpacing: '0.07em',
            textTransform: 'uppercase',
            display: 'block',
            mb: 0.375,
          }}
        >
          Experiment Name
        </Typography>
        <Box
          component="input"
          value={expName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setExpName(e.target.value)}
          sx={{
            display: 'block',
            width: '100%',
            border: 'none',
            outline: 'none',
            fontSize: 20,
            fontWeight: 800,
            color: 'primary.main',
            fontFamily: 'inherit',
            bgcolor: 'transparent',
            mb: 1.5,
          }}
        />
        <Typography
          sx={{
            fontWeight: 700,
            color: 'text.secondary',
            fontSize: 10.5,
            letterSpacing: '0.07em',
            textTransform: 'uppercase',
            display: 'block',
            mb: 0.375,
          }}
        >
          Research Question
        </Typography>
        <Box
          component="input"
          value={researchQ}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setResearchQ(e.target.value)}
          placeholder="What question is this experiment designed to answer?"
          sx={{
            display: 'block',
            width: '100%',
            border: 'none',
            outline: 'none',
            fontSize: 14,
            fontWeight: 500,
            color: '#1e293b',
            fontFamily: 'inherit',
            bgcolor: 'transparent',
            lineHeight: 1.6,
            '&::placeholder': { color: '#94a3b8' },
          }}
        />
      </Box>

      {/* ── Workflow stepper ── */}
      {(() => {
        interface Step {
          key: 'design' | 'conditions' | 'standards' | 'analysis' | 'review'
          label: string
          done: boolean
          status?: string
          onClick: () => void
        }
        const steps: Step[] = [
          {
            key: 'design',
            label: '① Framework',
            done: frameworkDone,
            status: frameworkDone ? 'ready' : 'write a framework',
            onClick: () => setActiveTab('design'),
          },
          {
            key: 'conditions',
            label: `② Conditions${conditions.length > 0 ? ` (${conditions.length})` : ''}`,
            done: conditionsDone,
            status: conditions.length === 0 ? 'no conditions' : conditionsDone ? 'ready' : 'assign prompts',
            onClick: () => setActiveTab('conditions'),
          },
          {
            key: 'standards',
            label: '③ Dataset',
            done: datasetDone,
            status: datasetDone
              ? `${kuCount} KUs extracted`
              : stdIds.length === 0
              ? 'no standards'
              : 'ready to generate',
            onClick: () => setActiveTab('standards'),
          },
          {
            key: 'analysis',
            label: '④ Analysis',
            done: false, // no hard done-signal for the mapping work yet
            status: kuCount === 0 ? 'generate first' : 'map KUs across passes',
            onClick: () => setActiveTab('analysis'),
          },
          {
            key: 'review',
            label: '⑤ Review',
            done: reviewCount > 0 && reviewCount === kuCount,
            status: kuCount === 0 ? 'generate first' : `${reviewCount}/${kuCount} reviewed`,
            onClick: () => navigate(`/experiments/${experiment.id}/review`),
          },
        ]
        return (
          <Box
            sx={{
              display: 'flex',
              flexShrink: 0,
              bgcolor: 'background.paper',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            {steps.map((step, i) => {
              const active = activeTab === step.key
              return (
                <Box
                  key={step.key}
                  onClick={step.onClick}
                  sx={{
                    flex: 1,
                    px: 1.5,
                    py: 1,
                    cursor: 'pointer',
                    borderBottom: active ? `2px solid ${sm.color}` : '2px solid transparent',
                    mb: '-1px',
                    bgcolor: active ? sm.bg : 'transparent',
                    '&:hover': { bgcolor: active ? sm.bg : '#f8fafc' },
                    transition: 'all 0.12s',
                    position: 'relative',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: active ? sm.color : step.done ? 'text.primary' : 'text.secondary',
                      }}
                    >
                      {step.label}
                    </Typography>
                    {step.done && <Typography sx={{ fontSize: 11, color: '#16a34a', fontWeight: 700 }}>✓</Typography>}
                  </Box>
                  {step.status && (
                    <Typography sx={{ fontSize: 10, color: 'text.disabled', mt: 0.25 }}>{step.status}</Typography>
                  )}
                  {i < steps.length - 1 && (
                    <Box
                      sx={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        bottom: 0,
                        width: '1px',
                        bgcolor: 'divider',
                      }}
                    />
                  )}
                </Box>
              )
            })}
          </Box>
        )
      })()}

      {/* ── Standards tab ── */}
      {activeTab === 'standards' && (
        <Box sx={{ flex: 1, overflowY: 'auto', px: 2, pb: 4 }}>
          {genError && (
            <Alert severity="error" sx={{ mt: 1.5, mb: 1 }} onClose={() => setGenError(null)}>
              {genError}
            </Alert>
          )}
          {generating && genProgress && (
            <Alert severity="info" sx={{ mt: 1.5, mb: 1 }} icon={<CircularProgress size={16} />}>
              {genProgress}
            </Alert>
          )}

          {/* Generate buttons + grade filter */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5, mb: 1, gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: 'text.secondary',
                  fontSize: 11,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                }}
              >
                Standards ({stdIds.length})
              </Typography>
              {conditions.length > 1 && (
                <Select
                  value={runConditionFilter}
                  onChange={(e) => setRunConditionFilter(e.target.value)}
                  size="small"
                  sx={{ fontSize: 11, height: 26, minWidth: 140 }}
                  title="Generation targets this condition only. 'All' runs every condition."
                >
                  <MenuItem value="all" sx={{ fontSize: 11 }}>
                    All conditions ({conditions.length})
                  </MenuItem>
                  {conditions.map((c) => (
                    <MenuItem key={c.id} value={c.id} sx={{ fontSize: 11 }}>
                      Only: {c.name}
                    </MenuItem>
                  ))}
                </Select>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {PASS_META.map((pass) => (
                <Button
                  key={pass.id}
                  size="small"
                  onClick={() => handleGenerate(pass.id)}
                  disabled={generating || summaryLoading}
                  title={`Generate ${pass.label} pass only`}
                  sx={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    color: pass.color,
                    bgcolor: pass.bg,
                    minWidth: 0,
                    px: 1.25,
                    '&:hover': { bgcolor: pass.bg, filter: 'brightness(0.95)' },
                  }}
                >
                  {pass.icon} {pass.label}
                </Button>
              ))}
              <Button
                size="small"
                onClick={handleGenerateAll}
                disabled={generating || summaryLoading}
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: 'primary.main',
                  bgcolor: '#e8edf7',
                  minWidth: 0,
                  px: 1.5,
                }}
              >
                {generating ? <CircularProgress size={14} /> : '▶ All'}
              </Button>
            </Box>
          </Box>

          {/* Grade filter pills */}
          {(() => {
            const byGrade: Record<string, string[]> = {}
            for (const g of grades) byGrade[g] = []
            for (const stdId of stdIds) {
              const meta = findStandard(stdId)
              if (meta && byGrade[meta.grade] !== undefined) byGrade[meta.grade].push(stdId)
            }
            const activeFilter = gradeFilter && byGrade[gradeFilter] ? gradeFilter : null
            const visibleIds = activeFilter ? byGrade[activeFilter] : stdIds
            return (
              <>
                <Box sx={{ display: 'flex', gap: 0.625, flexWrap: 'wrap', mb: 1.25 }}>
                  <Box
                    onClick={() => setGradeFilter(null)}
                    sx={{
                      px: 1.25,
                      py: 0.4,
                      borderRadius: 20,
                      cursor: 'pointer',
                      fontSize: 11,
                      fontWeight: 700,
                      border: '1.5px solid',
                      userSelect: 'none',
                      borderColor: !activeFilter ? sm.color : 'divider',
                      bgcolor: !activeFilter ? sm.color : 'background.paper',
                      color: !activeFilter ? '#fff' : 'text.secondary',
                      transition: 'all 0.1s',
                    }}
                  >
                    All ({stdIds.length})
                  </Box>
                  {grades.map((g) => {
                    const count = byGrade[g]?.length ?? 0
                    const active = activeFilter === g
                    return (
                      <Box
                        key={g}
                        onClick={() => setGradeFilter(active ? null : g)}
                        sx={{
                          px: 1.25,
                          py: 0.4,
                          borderRadius: 20,
                          cursor: 'pointer',
                          fontSize: 11,
                          fontWeight: 700,
                          border: '1.5px solid',
                          userSelect: 'none',
                          borderColor: active ? sm.color : 'divider',
                          bgcolor: active ? sm.color : 'background.paper',
                          color: active ? '#fff' : 'text.secondary',
                          transition: 'all 0.1s',
                        }}
                      >
                        {gradeLabel(g)} ({count})
                      </Box>
                    )
                  })}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 2 }}>
                  {visibleIds.map((stdId) => {
                    const meta = findStandard(stdId)
                    if (!meta) return null
                    const stdKUs = kuSummary.filter((k) => k.standard_id === stdId)
                    const hasData = stdsWithKUs.has(stdId)
                    const acceptedCount = stdKUs.filter((k) => k.admin_decision === 'accepted').length
                    const dotColor = acceptedCount > 0 ? '#16a34a' : hasData ? '#d97706' : '#e5e7eb'
                    const isSelected = selectedStdId === stdId
                    return (
                      <Box
                        key={stdId}
                        onClick={() => setSelectedStdId(isSelected ? null : stdId)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.25,
                          px: 1.5,
                          py: 1.25,
                          bgcolor: isSelected ? sm.bg : 'background.paper',
                          borderRadius: 1.5,
                          border: '2px solid',
                          borderColor: isSelected ? sm.color : 'divider',
                          cursor: 'pointer',
                          '&:hover': { bgcolor: isSelected ? sm.bg : 'grey.50' },
                        }}
                      >
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: dotColor, flexShrink: 0 }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 700, color: 'primary.main', display: 'block', mb: 0.15 }}
                          >
                            {stdId}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              fontSize: 11,
                              lineHeight: 1.35,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              display: 'block',
                            }}
                          >
                            {meta.std.text}
                          </Typography>
                        </Box>
                        {acceptedCount > 0 && (
                          <Chip
                            label={`✓${acceptedCount}`}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: 10,
                              bgcolor: '#dcfce7',
                              color: '#16a34a',
                              fontWeight: 700,
                              flexShrink: 0,
                            }}
                          />
                        )}
                        {isSelected && (
                          <Typography sx={{ fontSize: 10, color: sm.color, fontWeight: 700, flexShrink: 0 }}>
                            ▼ FOCUSED
                          </Typography>
                        )}
                      </Box>
                    )
                  })}
                </Box>
              </>
            )
          })()}

          {/* ── Focus panel: per-standard pass-by-pass work ── */}
          {selectedStdId &&
            (() => {
              const focusMeta = findStandard(selectedStdId)
              if (!focusMeta) return null
              // Group KUs by pass → run
              const byPass: Record<PassId, Record<string, KnowledgeUnit[]>> = {
                pass1: {},
                pass2: {},
                pass3: {},
              }
              for (const ku of detailKUs) {
                const pid = ku.pass_id
                const rid = ku.run_id
                if (!byPass[pid][rid]) byPass[pid][rid] = []
                byPass[pid][rid].push(ku)
              }
              return (
                <Box
                  sx={{
                    mt: 1,
                    mb: 2,
                    border: '2px solid',
                    borderColor: sm.color,
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    overflow: 'hidden',
                  }}
                >
                  {/* Focus header: standard text + close */}
                  <Box sx={{ p: 2, bgcolor: sm.bg, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={selectedStdId}
                          size="small"
                          sx={{ bgcolor: 'white', color: sm.color, fontWeight: 800, fontSize: 11 }}
                        />
                        <Typography variant="caption" sx={{ color: sm.color, fontWeight: 700, fontSize: 11 }}>
                          Grade {focusMeta.grade}
                          {focusMeta.domain ? ` · ${focusMeta.domain}` : ''}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => setSelectedStdId(null)}
                        sx={{ color: 'text.secondary' }}
                        title="Close focus"
                      >
                        ✕
                      </IconButton>
                    </Box>
                    <Typography sx={{ fontSize: 13, color: 'text.primary', lineHeight: 1.55 }}>
                      {focusMeta.std.text}
                    </Typography>
                    {/* Per-pass generate buttons */}
                    <Box sx={{ display: 'flex', gap: 0.75, mt: 1.5, flexWrap: 'wrap' }}>
                      {PASS_META.map((pass) => {
                        const existingRuns = Object.keys(byPass[pass.id]).length
                        return (
                          <Button
                            key={pass.id}
                            size="small"
                            variant="contained"
                            disabled={generating}
                            onClick={() => handleGenerateStandardPass(selectedStdId, pass.id)}
                            sx={{
                              fontSize: 11,
                              fontWeight: 700,
                              bgcolor: pass.color,
                              color: 'white',
                              '&:hover': { bgcolor: pass.color, filter: 'brightness(0.9)' },
                            }}
                          >
                            {pass.icon} {existingRuns > 0 ? '↻' : '▶'} {pass.label}
                            {existingRuns > 0 ? ` (${existingRuns} run${existingRuns !== 1 ? 's' : ''})` : ''}
                          </Button>
                        )
                      })}
                      <Typography
                        onClick={() => navigate(`/standard/${selectedStdId}`)}
                        sx={{
                          fontSize: 11,
                          color: 'text.secondary',
                          alignSelf: 'center',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          ml: 'auto',
                          '&:hover': { color: 'text.primary' },
                        }}
                        title="Open full standard detail (leaves experiment)"
                      >
                        Open full detail ›
                      </Typography>
                    </Box>
                  </Box>

                  {/* Pass-by-pass KU display */}
                  <Box sx={{ p: 2 }}>
                    {detailLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={20} />
                      </Box>
                    ) : detailKUs.length === 0 ? (
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 12 }}>
                        No KUs yet — click a pass button above to generate.
                      </Typography>
                    ) : (
                      PASS_META.map((pass) => {
                        const runIds = Object.keys(byPass[pass.id])
                        if (runIds.length === 0) return null
                        return (
                          <Box key={pass.id} sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                              <Typography sx={{ fontSize: 12, fontWeight: 700, color: pass.color }}>
                                {pass.icon} {pass.label}
                              </Typography>
                              <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
                                {runIds.length} run{runIds.length !== 1 ? 's' : ''}
                              </Typography>
                            </Box>
                            {runIds.map((rid) => {
                              const kus = byPass[pass.id][rid]
                              const run = kus[0]?.run
                              const isExpanded = expandedRunId === rid
                              const condName = run?.condition_id
                                ? conditions.find((c) => c.id === run.condition_id)?.name || '—'
                                : '—'
                              return (
                                <Box
                                  key={rid}
                                  sx={{
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1.5,
                                    mb: 1,
                                    overflow: 'hidden',
                                  }}
                                >
                                  {/* Run header */}
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1,
                                      px: 1.5,
                                      py: 1,
                                      bgcolor: 'grey.50',
                                      flexWrap: 'wrap',
                                    }}
                                  >
                                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.primary' }}>
                                      {kus.length} KU{kus.length !== 1 ? 's' : ''}
                                    </Typography>
                                    <Chip
                                      label={condName}
                                      size="small"
                                      sx={{ height: 18, fontSize: 10, fontWeight: 600 }}
                                    />
                                    <Chip
                                      label={run?.prompt_label || '—'}
                                      size="small"
                                      sx={{ height: 18, fontSize: 10, bgcolor: '#eef2ff', color: '#4338ca' }}
                                    />
                                    <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>
                                      {run?.model} · {run?.latency_ms ? `${(run.latency_ms / 1000).toFixed(1)}s` : '—'}
                                      {run?.input_tokens ? ` · ${run.input_tokens}in/${run.output_tokens ?? 0}out` : ''}
                                    </Typography>
                                    {run?.created_at && (
                                      <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>
                                        · {new Date(run.created_at).toLocaleString()}
                                      </Typography>
                                    )}
                                    <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
                                      <Button
                                        size="small"
                                        onClick={() => setExpandedRunId(isExpanded ? null : rid)}
                                        sx={{ fontSize: 10.5, fontWeight: 700, minWidth: 0 }}
                                      >
                                        {isExpanded ? '▲ Hide prompt' : '🔍 Inspect prompt'}
                                      </Button>
                                      <Button
                                        size="small"
                                        onClick={() => handleDeleteRun(rid)}
                                        title="Delete this run and its KUs"
                                        sx={{
                                          fontSize: 10.5,
                                          fontWeight: 700,
                                          minWidth: 0,
                                          color: '#dc2626',
                                          '&:hover': { bgcolor: '#fee2e2' },
                                        }}
                                      >
                                        🗑 Delete
                                      </Button>
                                    </Box>
                                  </Box>

                                  {/* KU list */}
                                  <Box sx={{ px: 1.5, py: 1 }}>
                                    {kus.map((ku, i) => (
                                      <Box
                                        key={ku.id}
                                        sx={{
                                          py: 0.75,
                                          borderBottom: i < kus.length - 1 ? '1px dashed' : 'none',
                                          borderColor: 'divider',
                                          cursor: 'pointer',
                                          '&:hover': { bgcolor: 'grey.50' },
                                        }}
                                        onClick={() => navigate(`/standard/${selectedStdId}/ku/${ku.id}`)}
                                      >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                                          <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'primary.main' }}>
                                            {ku.name}
                                          </Typography>
                                          {(() => {
                                            // Show confirmed type if present; otherwise fall back to the
                                            // LLM's proposed_type with an italic hint that it's unconfirmed.
                                            const shownType = ku.type || ku.proposed_type
                                            if (!shownType) return null
                                            const isProposed = !ku.type
                                            const typeMeta = KU_TYPES.find((t) => t.value === shownType)
                                            return (
                                              <Chip
                                                label={isProposed ? `${shownType} (LLM)` : shownType}
                                                size="small"
                                                sx={{
                                                  height: 16,
                                                  fontSize: 9,
                                                  bgcolor: (typeMeta?.color || '#666') + (isProposed ? '11' : '22'),
                                                  color: typeMeta?.color || '#666',
                                                  fontWeight: 700,
                                                  fontStyle: isProposed ? 'italic' : 'normal',
                                                  border: isProposed
                                                    ? `1px dashed ${typeMeta?.color || '#666'}`
                                                    : 'none',
                                                }}
                                              />
                                            )
                                          })()}
                                          {ku.admin_decision && (
                                            <Chip
                                              label={ku.admin_decision}
                                              size="small"
                                              sx={{
                                                height: 16,
                                                fontSize: 9,
                                                bgcolor:
                                                  ku.admin_decision === 'accepted'
                                                    ? '#dcfce7'
                                                    : ku.admin_decision === 'rejected'
                                                    ? '#fee2e2'
                                                    : '#fef3c7',
                                                color:
                                                  ku.admin_decision === 'accepted'
                                                    ? '#16a34a'
                                                    : ku.admin_decision === 'rejected'
                                                    ? '#dc2626'
                                                    : '#b45309',
                                                fontWeight: 700,
                                              }}
                                            />
                                          )}
                                        </Box>
                                        <Typography
                                          sx={{
                                            fontSize: 11,
                                            color: 'text.secondary',
                                            lineHeight: 1.45,
                                          }}
                                        >
                                          {ku.description}
                                        </Typography>
                                      </Box>
                                    ))}
                                  </Box>

                                  {/* Prompt inspector */}
                                  {isExpanded && run && (
                                    <Box
                                      sx={{
                                        bgcolor: '#0f172a',
                                        color: '#e2e8f0',
                                        p: 1.5,
                                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                        fontSize: 10.5,
                                        borderTop: '1px solid',
                                        borderColor: 'divider',
                                      }}
                                    >
                                      {run.system_prompt_snapshot && (
                                        <Box sx={{ mb: 1.5 }}>
                                          <Typography
                                            sx={{
                                              fontSize: 10,
                                              fontWeight: 700,
                                              color: '#fbbf24',
                                              mb: 0.5,
                                              letterSpacing: '0.05em',
                                            }}
                                          >
                                            SYSTEM PROMPT
                                          </Typography>
                                          <Box
                                            component="pre"
                                            sx={{
                                              whiteSpace: 'pre-wrap',
                                              wordBreak: 'break-word',
                                              m: 0,
                                              fontSize: 10.5,
                                              lineHeight: 1.5,
                                            }}
                                          >
                                            {run.system_prompt_snapshot}
                                          </Box>
                                        </Box>
                                      )}
                                      {run.source_doc &&
                                        (() => {
                                          const stdMeta = findStandard(run.standard_id)
                                          const expectedStyle: 'pedagogy' | 'article' =
                                            run.pass_id === 'pass2' ? 'pedagogy' : 'article'
                                          const t1 = computeTier1(
                                            run.source_doc,
                                            stdMeta?.std.text || '',
                                            expectedStyle,
                                            stdMeta?.grade,
                                          )
                                          return (
                                            <Box sx={{ mb: 1.5 }}>
                                              <Typography
                                                sx={{
                                                  fontSize: 10,
                                                  fontWeight: 700,
                                                  color: '#a78bfa',
                                                  mb: 0.5,
                                                  letterSpacing: '0.05em',
                                                }}
                                              >
                                                SOURCE DOC ({expectedStyle === 'pedagogy' ? 'lesson plan' : 'article'})
                                              </Typography>
                                              {/* Tier 1 QA chip strip */}
                                              <Box
                                                sx={{
                                                  display: 'flex',
                                                  gap: 0.5,
                                                  flexWrap: 'wrap',
                                                  mb: 0.5,
                                                  p: 0.5,
                                                  bgcolor: 'rgba(255,255,255,0.05)',
                                                  borderRadius: 0.5,
                                                }}
                                              >
                                                <Chip
                                                  label={`${t1.wordCount} words`}
                                                  size="small"
                                                  sx={{
                                                    height: 16,
                                                    fontSize: 9,
                                                    fontWeight: 700,
                                                    bgcolor: '#475569',
                                                    color: 'white',
                                                  }}
                                                />
                                                <Chip
                                                  label={`FK grade ${t1.fleschKincaidGrade.toFixed(1)}`}
                                                  size="small"
                                                  sx={{
                                                    height: 16,
                                                    fontSize: 9,
                                                    fontWeight: 700,
                                                    bgcolor: '#475569',
                                                    color: 'white',
                                                  }}
                                                />
                                                <Chip
                                                  label={`Coverage ${(t1.keyTermCoverage * 100).toFixed(0)}%`}
                                                  size="small"
                                                  sx={{
                                                    height: 16,
                                                    fontSize: 9,
                                                    fontWeight: 700,
                                                    bgcolor: t1.keyTermCoverage < 0.5 ? '#dc2626' : '#475569',
                                                    color: 'white',
                                                  }}
                                                />
                                                <Chip
                                                  label={`Style: ${t1.styleFit}`}
                                                  size="small"
                                                  title={t1.styleReason}
                                                  sx={{
                                                    height: 16,
                                                    fontSize: 9,
                                                    fontWeight: 700,
                                                    bgcolor:
                                                      t1.styleFit === 'match'
                                                        ? '#16a34a'
                                                        : t1.styleFit === 'drift'
                                                        ? '#dc2626'
                                                        : '#d97706',
                                                    color: 'white',
                                                  }}
                                                />
                                                {t1.flags.map((f, i) => (
                                                  <Chip
                                                    key={i}
                                                    label={`⚠ ${f}`}
                                                    size="small"
                                                    sx={{
                                                      height: 16,
                                                      fontSize: 9,
                                                      fontWeight: 700,
                                                      bgcolor: '#b45309',
                                                      color: 'white',
                                                    }}
                                                  />
                                                ))}
                                              </Box>
                                              <Box
                                                component="pre"
                                                sx={{
                                                  whiteSpace: 'pre-wrap',
                                                  wordBreak: 'break-word',
                                                  m: 0,
                                                  fontSize: 10.5,
                                                  lineHeight: 1.5,
                                                }}
                                              >
                                                {run.source_doc}
                                              </Box>
                                            </Box>
                                          )
                                        })()}
                                      <Box sx={{ mb: 1.5 }}>
                                        <Typography
                                          sx={{
                                            fontSize: 10,
                                            fontWeight: 700,
                                            color: '#34d399',
                                            mb: 0.5,
                                            letterSpacing: '0.05em',
                                          }}
                                        >
                                          USER PROMPT ({run.prompt_slot})
                                        </Typography>
                                        <Box
                                          component="pre"
                                          sx={{
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                            m: 0,
                                            fontSize: 10.5,
                                            lineHeight: 1.5,
                                          }}
                                        >
                                          {run.prompt_text}
                                        </Box>
                                      </Box>
                                    </Box>
                                  )}
                                </Box>
                              )
                            })}
                          </Box>
                        )
                      })
                    )}
                  </Box>
                </Box>
              )
            })()}

          {/* Type distribution */}
          {acceptedKUs.length > 0 && (
            <Box
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 1.5,
                p: 1.75,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: 'text.secondary',
                  fontSize: 11,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  display: 'block',
                  mb: 1.25,
                }}
              >
                Type Distribution — {acceptedKUs.length} accepted KUs
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.875 }}>
                {KU_TYPES.filter((t) => t.value !== 'unclear').map((t) => {
                  const n = typeDist[t.value] || 0
                  const pct = acceptedKUs.length ? Math.round((n / acceptedKUs.length) * 100) : 0
                  return (
                    <Box key={t.value}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.375 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: t.color, fontSize: 11 }}>
                          {t.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                          {n} ({pct}%)
                        </Typography>
                      </Box>
                      <Box sx={{ height: 5, bgcolor: 'grey.100', borderRadius: 1, overflow: 'hidden' }}>
                        <Box
                          sx={{
                            height: '100%',
                            width: `${pct}%`,
                            bgcolor: t.color,
                            borderRadius: 1,
                            transition: 'width 0.4s ease',
                          }}
                        />
                      </Box>
                    </Box>
                  )
                })}
                {(typeDist['unclear'] || 0) > 0 && (
                  <Typography variant="caption" sx={{ color: 'error.main', fontSize: 11, mt: 0.5 }}>
                    ⚠ {typeDist['unclear']} KU{typeDist['unclear'] !== 1 ? 's' : ''} marked Unclear — key research
                    finding
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ display: 'block', textAlign: 'center', mt: 2, fontSize: 10 }}
          >
            Created {new Date(experiment.created_at).toLocaleDateString()}
          </Typography>
        </Box>
      )}

      {/* ── Conditions tab ── */}
      {activeTab === 'conditions' && (
        <Box sx={{ flex: 1, overflowY: 'auto', px: 2, pb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, mb: 1.5 }}>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 13, color: 'primary.main' }}>
                Experimental Conditions
              </Typography>
              <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                Each condition assigns its own prompts per pass. Running generation creates one KU set per condition.
              </Typography>
            </Box>
            <Button variant="contained" size="small" onClick={openNewCondition} sx={{ fontSize: 11, fontWeight: 700 }}>
              + New Condition
            </Button>
          </Box>

          {conditions.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6, bgcolor: '#f8fafc', borderRadius: 2, border: '1px dashed #cbd5e1' }}>
              <Typography sx={{ fontSize: 14, color: 'text.secondary', mb: 0.5 }}>No conditions defined</Typography>
              <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
                Add conditions to compare different prompt strategies (e.g. zero-shot vs. expert-guided).
              </Typography>
            </Box>
          ) : (
            conditions.map((c) => (
              <Box
                key={c.id}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  p: 1.75,
                  mb: 1.25,
                  bgcolor: 'background.paper',
                }}
              >
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.25 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 13, color: 'primary.main' }}>{c.name}</Typography>
                    {c.description && (
                      <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.25 }}>{c.description}</Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton size="small" onClick={() => openEditCondition(c)} sx={{ color: 'text.secondary' }}>
                      <EditOutlinedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton size="small" onClick={() => setDeletingCondition(c)} sx={{ color: '#dc2626' }}>
                      <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                </Box>

                {/* Prompt assignments per pass */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  {PASS_META.map((pass) => (
                    <Box key={pass.id} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Box
                        sx={{
                          minWidth: 110,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          fontSize: 11,
                          fontWeight: 700,
                          color: pass.color,
                        }}
                      >
                        <span>{pass.icon}</span>
                        <span>{pass.label}</span>
                      </Box>
                      {pass.hasGenerate && (
                        <FormControl size="small" sx={{ flex: 1, minWidth: 140 }}>
                          <InputLabel sx={{ fontSize: 11 }}>Source Material Prompt</InputLabel>
                          <Select
                            value={getAssignedPromptId(c.id, pass.id, 'generate')}
                            label="Source Material Prompt"
                            onChange={(e) => handlePromptChange(c.id, pass.id, 'generate', e.target.value)}
                            sx={{ fontSize: 11 }}
                          >
                            {getPromptOptions(pass.id, 'generate').map((p) => (
                              <MenuItem key={p.id} value={p.id} sx={{ fontSize: 11 }}>
                                {p.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                      <FormControl size="small" sx={{ flex: 1, minWidth: 140 }}>
                        <InputLabel sx={{ fontSize: 11 }}>KU Extraction Prompt</InputLabel>
                        <Select
                          value={getAssignedPromptId(c.id, pass.id, 'extract')}
                          label="KU Extraction Prompt"
                          onChange={(e) => handlePromptChange(c.id, pass.id, 'extract', e.target.value)}
                          sx={{ fontSize: 11 }}
                        >
                          {getPromptOptions(pass.id, 'extract').map((p) => (
                            <MenuItem key={p.id} value={p.id} sx={{ fontSize: 11 }}>
                              {p.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  ))}
                </Box>

                {/* Summary line */}
                <Typography sx={{ fontSize: 10, color: 'text.disabled', mt: 1.25 }}>
                  {getPromptsForCondition(c.id).length} prompt{getPromptsForCondition(c.id).length !== 1 ? 's' : ''}{' '}
                  assigned
                </Typography>
              </Box>
            ))
          )}

          {/* AI-drafted experimental design description */}
          {conditions.length > 0 && (
            <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 1 }}>
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: 11,
                      color: 'text.secondary',
                      letterSpacing: '0.05em',
                      textTransform: 'uppercase',
                    }}
                  >
                    Experiment Condition Description
                  </Typography>
                  <Typography sx={{ fontSize: 10.5, color: 'text.disabled', mt: 0.25 }}>
                    AI-drafted paragraph for the Methods section. Review and edit before publishing.
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleDraftConditions}
                  disabled={draftingConditions}
                  sx={{ fontSize: 11, fontWeight: 700, minWidth: 0, px: 1.5 }}
                >
                  {draftingConditions ? (
                    <CircularProgress size={14} />
                  ) : conditionsDescription ? (
                    '↻ Re-draft'
                  ) : (
                    '✨ AI Draft'
                  )}
                </Button>
              </Box>

              {draftConditionsError && (
                <Alert severity="error" sx={{ mb: 1, fontSize: 11 }} onClose={() => setDraftConditionsError(null)}>
                  {draftConditionsError}
                </Alert>
              )}

              <TextField
                multiline
                minRows={8}
                maxRows={25}
                fullWidth
                placeholder="Click AI Draft to generate, or write your own."
                value={conditionsDescription}
                onChange={(e) => setConditionsDescription(e.target.value)}
                size="small"
                sx={{
                  '& .MuiInputBase-root': { fontSize: 13, lineHeight: 1.6 },
                  bgcolor: 'background.paper',
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5, px: 0.5 }}>
                <Typography sx={{ fontSize: 10.5, color: 'text.disabled' }}>
                  {conditionsDescription.length.toLocaleString()} chars ·{' '}
                  {conditionsDescription.trim() ? conditionsDescription.trim().split(/\s+/).length.toLocaleString() : 0}{' '}
                  words
                </Typography>
                {conditionsDescription.trim() && (
                  <Button
                    size="small"
                    onClick={() => {
                      navigator.clipboard.writeText(conditionsDescription)
                      setSaveStatus('saved')
                      setTimeout(() => setSaveStatus('idle'), 1000)
                    }}
                    sx={{ fontSize: 10.5, fontWeight: 700, minWidth: 0, px: 1 }}
                  >
                    📋 Copy
                  </Button>
                )}
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* ── Begin Experiment confirm dialog ── */}
      {(() => {
        const kuDeleteCount = kuSummary.length
        const hasDataAtRisk = kuDeleteCount > 0
        const nameConfirmed = beginConfirmName.trim() === experiment.name.trim()
        return (
          <Dialog
            open={beginOpen}
            onClose={() => {
              if (!beginRunning) {
                setBeginOpen(false)
                setBeginConfirmName('')
              }
            }}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ fontSize: 15, fontWeight: 800, color: hasDataAtRisk ? '#b91c1c' : 'primary.main' }}>
              {hasDataAtRisk ? '⚠ Destructive: Begin Experiment' : 'Begin Experiment'}
            </DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: '8px !important' }}>
              {hasDataAtRisk && (
                <Alert severity="error" sx={{ fontSize: 13 }}>
                  <strong>
                    This will permanently delete {kuDeleteCount} KU{kuDeleteCount !== 1 ? 's' : ''}
                  </strong>{' '}
                  and all runs tagged to this experiment. This cannot be undone from the app.
                </Alert>
              )}
              <Typography sx={{ fontSize: 13, color: 'text.primary' }}>
                Clicking begins <strong>collection phase</strong>. Once begun:
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { fontSize: 12.5, mb: 0.5, lineHeight: 1.55 } }}>
                <li>
                  <strong>Framework, system prompt, standards, and taxonomy</strong> become locked. Edits require a new
                  experiment.
                </li>
                <li>
                  <strong>Any pilot KUs and runs</strong> tagged to this experiment are{' '}
                  <strong style={{ color: '#b91c1c' }}>permanently deleted</strong>.
                </li>
                <li>Each condition can then be independently started (prompt-snapshotted), locked, and published.</li>
              </Box>
              <Typography sx={{ fontSize: 12, color: 'text.secondary', fontStyle: 'italic' }}>
                There is no unlock. If you need to change the framework or standards later, duplicate this experiment
                into a new one.
              </Typography>
              {hasDataAtRisk && (
                <Box>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#b91c1c', mb: 0.5 }}>
                    Type the experiment name to confirm:{' '}
                    <code style={{ background: '#fee2e2', padding: '1px 4px', borderRadius: 3 }}>
                      {experiment.name}
                    </code>
                  </Typography>
                  <TextField
                    value={beginConfirmName}
                    onChange={(e) => setBeginConfirmName(e.target.value)}
                    size="small"
                    fullWidth
                    autoFocus
                    placeholder={experiment.name}
                    sx={{ '& input': { fontSize: 13 } }}
                  />
                </Box>
              )}
              {beginError && (
                <Alert severity="error" onClose={() => setBeginError(null)}>
                  {beginError}
                </Alert>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button
                onClick={() => {
                  setBeginOpen(false)
                  setBeginConfirmName('')
                }}
                disabled={beginRunning}
                sx={{ color: 'text.secondary' }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleBegin}
                disabled={beginRunning || (hasDataAtRisk && !nameConfirmed)}
                sx={{
                  bgcolor: hasDataAtRisk ? '#b91c1c' : '#16a34a',
                  '&:hover': { bgcolor: hasDataAtRisk ? '#991b1b' : '#15803d' },
                  fontWeight: 700,
                }}
              >
                {beginRunning ? (
                  <CircularProgress size={16} sx={{ color: 'white' }} />
                ) : hasDataAtRisk ? (
                  `🗑 Delete ${kuDeleteCount} KUs & Begin`
                ) : (
                  'Begin'
                )}
              </Button>
            </DialogActions>
          </Dialog>
        )
      })()}

      {/* ── Edit condition dialog ── */}
      <Dialog open={!!editingCondition} onClose={() => setEditingCondition(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 700 }}>
          {editingCondition?.id ? 'Edit Condition' : 'New Condition'}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <TextField
            label="Name"
            placeholder="e.g. Zero-Shot"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            size="small"
            fullWidth
            autoFocus
          />
          <TextField
            label="Description (optional)"
            placeholder="e.g. Minimal prompt without CDT framework"
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            size="small"
            fullWidth
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditingCondition(null)} sx={{ color: 'text.secondary' }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={saveCondition} disabled={!editName.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete condition confirm dialog ── */}
      <Dialog open={!!deletingCondition} onClose={() => setDeletingCondition(null)} maxWidth="xs">
        <DialogTitle sx={{ fontSize: 15, fontWeight: 700 }}>Delete Condition</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13 }}>
            Delete condition <strong>{deletingCondition?.name}</strong>? Any runs and KUs tagged with this condition
            will keep the reference but become orphaned.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeletingCondition(null)} sx={{ color: 'text.secondary' }}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={confirmDeleteCondition}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Analysis tab: manual KU mapping across passes ── */}
      {activeTab === 'analysis' && (
        <Box sx={{ flex: 1, overflowY: 'auto', px: 2, pb: 4 }}>
          {(() => {
            // Group KUs by standard_id × pass_id for the three-column view
            const byStdPass: Record<string, Record<PassId, KnowledgeUnit[]>> = {}
            for (const ku of analysisAllKUs) {
              if (!byStdPass[ku.standard_id]) {
                byStdPass[ku.standard_id] = { pass1: [], pass2: [], pass3: [] }
              }
              byStdPass[ku.standard_id][ku.pass_id].push(ku)
            }
            const stdIdsWithKUs = Object.keys(byStdPass).sort()
            const activeStd = analysisStdId || stdIdsWithKUs[0] || null
            const stdBuckets = activeStd ? byStdPass[activeStd] : null

            if (stdIdsWithKUs.length === 0) {
              return (
                <Box sx={{ textAlign: 'center', pt: 6 }}>
                  <Typography sx={{ fontSize: 32, mb: 1.5 }}>🗺</Typography>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'primary.main', mb: 0.5 }}>
                    No KUs to analyze yet
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                    Generate KUs on the Dataset tab first. Then come back here to map REQ → CONT → KNOW.
                  </Typography>
                </Box>
              )
            }

            // Metrics for the active standard
            const reqKUs = stdBuckets?.pass1 || []
            const contKUs = stdBuckets?.pass2 || []
            const knowKUs = stdBuckets?.pass3 || []
            const reqAddressed = reqKUs.filter((r) => contKUs.some((c) => isLinked(c.id, r.id, 'addresses'))).length
            const contGrounded = contKUs.filter((c) => knowKUs.some((k) => isLinked(c.id, k.id, 'grounds_in'))).length
            const orphanCont = contKUs.filter((c) => !reqKUs.some((r) => isLinked(c.id, r.id, 'addresses'))).length
            const orphanKnow = knowKUs.filter((k) => !contKUs.some((c) => isLinked(c.id, k.id, 'grounds_in'))).length

            // Classification-agreement across linked pairs for this standard.
            // A "correct match" requires both a cross-pass link AND matching
            // classification labels. We count per-relation and overall.
            const kuById = new Map(analysisAllKUs.map((k) => [k.id, k]))
            const labelOf = (k: KnowledgeUnit | undefined) => (k ? k.type || k.proposed_type : null)
            const stdLinks = crossRefs.filter((r) => {
              const a = kuById.get(r.from_ku_id)
              const b = kuById.get(r.to_ku_id)
              return a && b && a.standard_id === activeStd && b.standard_id === activeStd
            })
            const totalLinksHere = stdLinks.length
            const matchingLinks = stdLinks.filter((r) => {
              const a = labelOf(kuById.get(r.from_ku_id))
              const b = labelOf(kuById.get(r.to_ku_id))
              return a && b && a === b
            }).length
            const mismatchLinks = totalLinksHere - matchingLinks
            // Same-KU with divergent classification — the headline taxonomy-ambiguity
            // finding. Only count same_as pairs where both sides have a type and they differ.
            const sameAsWithClassMismatch = stdLinks.filter((r) => {
              if (r.relation_type !== 'same_as') return false
              const a = labelOf(kuById.get(r.from_ku_id))
              const b = labelOf(kuById.get(r.to_ku_id))
              return a && b && a !== b
            }).length
            // Helper for the sidebar list — is this specific row a divergent-class same_as?
            const rowHasClassMismatch = (r: (typeof crossRefs)[number]) => {
              if (r.relation_type !== 'same_as') return false
              const a = labelOf(kuById.get(r.from_ku_id))
              const b = labelOf(kuById.get(r.to_ku_id))
              return !!(a && b && a !== b)
            }

            return (
              <>
                {/* Standard selector + view toggle */}
                <Box sx={{ mt: 1.5, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary' }}>Standard:</Typography>
                  <Select
                    value={activeStd || ''}
                    onChange={(e) => {
                      setAnalysisStdId(e.target.value)
                      setPickedKuId(null)
                    }}
                    size="small"
                    sx={{ fontSize: 12, minWidth: 260 }}
                  >
                    {stdIdsWithKUs.map((stdId) => (
                      <MenuItem key={stdId} value={stdId} sx={{ fontSize: 12 }}>
                        {stdId}
                      </MenuItem>
                    ))}
                  </Select>
                  <Box sx={{ ml: 'auto', display: 'flex', gap: 0 }}>
                    <Button
                      variant={analysisView === 'matrix' ? 'contained' : 'outlined'}
                      size="small"
                      onClick={() => setAnalysisView('matrix')}
                      sx={{ fontSize: 11, fontWeight: 700, borderRadius: '4px 0 0 4px' }}
                    >
                      ▦ Matrix
                    </Button>
                    <Button
                      variant={analysisView === 'graph' ? 'contained' : 'outlined'}
                      size="small"
                      onClick={() => setAnalysisView('graph')}
                      sx={{ fontSize: 11, fontWeight: 700, borderRadius: 0 }}
                    >
                      ⊙ Graph
                    </Button>
                    <Button
                      variant={analysisView === 'reliability' ? 'contained' : 'outlined'}
                      size="small"
                      onClick={() => setAnalysisView('reliability')}
                      sx={{ fontSize: 11, fontWeight: 700, borderRadius: '0 4px 4px 0' }}
                    >
                      📊 Reliability
                    </Button>
                  </Box>
                </Box>

                {/* Summary metrics */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    mb: 1.5,
                    flexWrap: 'wrap',
                  }}
                >
                  <Chip
                    label={`REQ → CONT: ${reqAddressed}/${reqKUs.length} (${
                      reqKUs.length ? Math.round((reqAddressed / reqKUs.length) * 100) : 0
                    }%)`}
                    sx={{ fontSize: 11, fontWeight: 700, bgcolor: '#e0f2fe', color: '#0369a1' }}
                  />
                  <Chip
                    label={`CONT → KNOW: ${contGrounded}/${contKUs.length} (${
                      contKUs.length ? Math.round((contGrounded / contKUs.length) * 100) : 0
                    }%)`}
                    sx={{ fontSize: 11, fontWeight: 700, bgcolor: '#f5f3ff', color: '#7c3aed' }}
                  />
                  <Chip
                    label={`Orphan CONT: ${orphanCont}`}
                    sx={{
                      fontSize: 11,
                      fontWeight: 700,
                      bgcolor: orphanCont > 0 ? '#fef3c7' : '#f0fdf4',
                      color: orphanCont > 0 ? '#92400e' : '#166534',
                    }}
                  />
                  <Chip
                    label={`Orphan KNOW: ${orphanKnow}`}
                    sx={{
                      fontSize: 11,
                      fontWeight: 700,
                      bgcolor: orphanKnow > 0 ? '#fef3c7' : '#f0fdf4',
                      color: orphanKnow > 0 ? '#92400e' : '#166534',
                    }}
                  />
                  {totalLinksHere > 0 && (
                    <Chip
                      label={`Class match: ${matchingLinks}/${totalLinksHere} (${Math.round(
                        (matchingLinks / totalLinksHere) * 100,
                      )}%)`}
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        bgcolor: mismatchLinks > 0 ? '#fffbeb' : '#f0fdf4',
                        color: mismatchLinks > 0 ? '#92400e' : '#166534',
                      }}
                    />
                  )}
                  {sameAsWithClassMismatch > 0 && (
                    <Chip
                      label={`Same-KU class ambiguity: ${sameAsWithClassMismatch}`}
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        bgcolor: '#fef3c7',
                        color: '#92400e',
                      }}
                      title="KU pairs marked as the same but classified differently — taxonomy-ambiguity signal"
                    />
                  )}
                </Box>

                {/* Graph view — read-only visualization of relations built in matrix */}
                {analysisView === 'graph' && (
                  <RelationshipGraph kus={analysisAllKUs.filter((k) => k.standard_id === activeStd)} refs={stdLinks} />
                )}

                {/* Reliability view — test-retest replicates analysis */}
                {analysisView === 'reliability' &&
                  (() => {
                    const stdKUs = analysisAllKUs.filter((k) => k.standard_id === activeStd)
                    type RunBucket = { runId: string; conditionId: string | null; kus: KnowledgeUnit[] }
                    const byPass: Record<PassId, RunBucket[]> = { pass1: [], pass2: [], pass3: [] }
                    for (const ku of stdKUs) {
                      const bucket = byPass[ku.pass_id].find(
                        (r) => r.runId === ku.run_id && r.conditionId === (ku.condition_id ?? null),
                      )
                      if (bucket) bucket.kus.push(ku)
                      else
                        byPass[ku.pass_id].push({
                          runId: ku.run_id,
                          conditionId: ku.condition_id ?? null,
                          kus: [ku],
                        })
                    }
                    const normName = (s: string) =>
                      s
                        .toLowerCase()
                        .replace(/[^a-z0-9\s]/g, '')
                        .replace(/\s+/g, ' ')
                        .trim()
                    const jaccard = (a: Set<string>, b: Set<string>): number => {
                      if (a.size === 0 && b.size === 0) return 1
                      let inter = 0
                      a.forEach((x) => {
                        if (b.has(x)) inter++
                      })
                      const uni = a.size + b.size - inter
                      return uni === 0 ? 0 : inter / uni
                    }
                    return (
                      <Box
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1.5,
                          p: 1.5,
                          bgcolor: '#fafbfc',
                        }}
                      >
                        <Typography sx={{ fontSize: 11, fontWeight: 800, color: 'primary.main', mb: 1 }}>
                          TEST-RETEST RELIABILITY (this standard)
                        </Typography>
                        {PASS_META.map((pass) => {
                          const runs = byPass[pass.id]
                          if (runs.length === 0) return null
                          const byCond = new Map<string, RunBucket[]>()
                          for (const r of runs) {
                            const key = r.conditionId || '(no condition)'
                            if (!byCond.has(key)) byCond.set(key, [])
                            byCond.get(key)!.push(r)
                          }
                          return (
                            <Box key={pass.id} sx={{ mb: 2 }}>
                              <Typography sx={{ fontSize: 12, fontWeight: 700, color: pass.color, mb: 0.5 }}>
                                {pass.icon} {pass.label}
                              </Typography>
                              {Array.from(byCond.entries()).map(([condKey, bucket]) => {
                                const condName =
                                  condKey === '(no condition)'
                                    ? '(no condition)'
                                    : conditions.find((c) => c.id === condKey)?.name || condKey
                                const n = bucket.length
                                if (n < 2) {
                                  return (
                                    <Typography key={condKey} sx={{ fontSize: 11, color: 'text.disabled', ml: 2 }}>
                                      {condName}: 1 run — no replicates to compare
                                    </Typography>
                                  )
                                }
                                const runSets = bucket.map((b) => ({
                                  runId: b.runId,
                                  count: b.kus.length,
                                  names: new Set(b.kus.map((k) => normName(k.name))),
                                }))
                                const counts = runSets.map((r) => r.count)
                                const mean = counts.reduce((a, b) => a + b, 0) / n
                                const variance = counts.reduce((acc, c) => acc + (c - mean) ** 2, 0) / n
                                const sd = Math.sqrt(variance)
                                const pairs: number[] = []
                                for (let i = 0; i < n; i++) {
                                  for (let j = i + 1; j < n; j++) {
                                    pairs.push(jaccard(runSets[i].names, runSets[j].names))
                                  }
                                }
                                const meanJaccard = pairs.length ? pairs.reduce((a, b) => a + b, 0) / pairs.length : 1
                                const threshold = Math.ceil(n / 2)
                                const nameCounts = new Map<string, number>()
                                runSets.forEach((r) =>
                                  r.names.forEach((x) => nameCounts.set(x, (nameCounts.get(x) ?? 0) + 1)),
                                )
                                const stableNames = Array.from(nameCounts.entries()).filter(([, c]) => c >= threshold)
                                return (
                                  <Box key={condKey} sx={{ ml: 2, mb: 1 }}>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        gap: 1,
                                        flexWrap: 'wrap',
                                        alignItems: 'center',
                                        mb: 0.5,
                                      }}
                                    >
                                      <Typography sx={{ fontSize: 11, fontWeight: 700 }}>{condName}</Typography>
                                      <Chip
                                        label={`${n} runs`}
                                        size="small"
                                        sx={{ height: 16, fontSize: 9, fontWeight: 700 }}
                                      />
                                      <Chip
                                        label={`count: ${mean.toFixed(1)} ± ${sd.toFixed(1)}`}
                                        size="small"
                                        sx={{
                                          height: 16,
                                          fontSize: 9,
                                          fontWeight: 700,
                                          bgcolor: '#e0e7ff',
                                          color: '#4338ca',
                                        }}
                                      />
                                      <Chip
                                        label={`J̄ = ${meanJaccard.toFixed(2)}`}
                                        title="Mean pairwise Jaccard similarity. 1.0 = identical, 0 = no overlap."
                                        size="small"
                                        sx={{
                                          height: 16,
                                          fontSize: 9,
                                          fontWeight: 700,
                                          bgcolor:
                                            meanJaccard >= 0.7 ? '#dcfce7' : meanJaccard >= 0.4 ? '#fef3c7' : '#fee2e2',
                                          color:
                                            meanJaccard >= 0.7 ? '#166534' : meanJaccard >= 0.4 ? '#92400e' : '#991b1b',
                                        }}
                                      />
                                      <Chip
                                        label={`stable: ${stableNames.length}`}
                                        title={`KU names appearing in ≥ ${threshold}/${n} runs.`}
                                        size="small"
                                        sx={{
                                          height: 16,
                                          fontSize: 9,
                                          fontWeight: 700,
                                          bgcolor: '#d1fae5',
                                          color: '#065f46',
                                        }}
                                      />
                                    </Box>
                                    {stableNames.length > 0 && (
                                      <Typography sx={{ fontSize: 10, color: 'text.secondary', lineHeight: 1.5 }}>
                                        Stable KUs: {stableNames.map(([nm, c]) => `${nm} (${c}/${n})`).join('; ')}
                                      </Typography>
                                    )}
                                  </Box>
                                )
                              })}
                            </Box>
                          )
                        })}
                        {stdKUs.length === 0 && (
                          <Typography sx={{ fontSize: 11, color: 'text.disabled', fontStyle: 'italic' }}>
                            No runs for this standard yet.
                          </Typography>
                        )}
                        <Typography sx={{ fontSize: 10, color: 'text.disabled', fontStyle: 'italic', mt: 1 }}>
                          Replicate via Workbench: tick &quot;Replicate (keep prior)&quot; on Save to Dataset and run
                          the same prompt N times. Typical target: N=5 per (standard × pass × condition).
                        </Typography>
                      </Box>
                    )
                  })()}

                {/* Two-column outer layout: sticky left sidebar + scrollable grid */}
                {analysisView === 'matrix' && (
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                    {/* ── LEFT SIDEBAR (sticky) ── */}
                    {(() => {
                      const kuA = pickedKuId ? analysisAllKUs.find((k) => k.id === pickedKuId) : null
                      const kuB = pickedPairKuId ? analysisAllKUs.find((k) => k.id === pickedPairKuId) : null
                      const canRecord = !!(kuA && kuB)
                      const record = async (rel: CrossRefRelation) => {
                        if (!kuA || !kuB) return
                        await linkKUs(kuA.id, kuB.id, rel, user?.id || null)
                        setPickedKuId(null)
                        setPickedPairKuId(null)
                      }
                      const swap = () => {
                        setPickedKuId(pickedPairKuId)
                        setPickedPairKuId(pickedKuId)
                      }
                      const clear = () => {
                        setPickedKuId(null)
                        setPickedPairKuId(null)
                      }
                      const descriptor = (k: KnowledgeUnit) => {
                        const p = PASS_META.find((pp) => pp.id === k.pass_id)
                        return `${p?.label || k.pass_id} · ${k.name}`
                      }
                      // Relations on this standard for the list
                      const stdLinksList = crossRefs.filter((r) => {
                        const a = kuById.get(r.from_ku_id)
                        const b = kuById.get(r.to_ku_id)
                        return a && b && a.standard_id === activeStd && b.standard_id === activeStd
                      })
                      return (
                        <Box sx={{ width: 300, flexShrink: 0, position: 'sticky', top: 0, alignSelf: 'flex-start' }}>
                          {/* Action buttons */}
                          <Box
                            sx={{
                              border: '1.5px solid',
                              borderColor: canRecord ? 'primary.main' : 'divider',
                              borderRadius: 1.5,
                              bgcolor: canRecord ? '#f0f9ff' : 'background.paper',
                              p: 1.25,
                              mb: 1.25,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: 10,
                                fontWeight: 800,
                                color: 'primary.main',
                                letterSpacing: '0.05em',
                                mb: 0.75,
                              }}
                            >
                              SET RELATION
                            </Typography>
                            {!kuA && (
                              <Typography sx={{ fontSize: 12, color: 'text.secondary', fontStyle: 'italic' }}>
                                Click a KU to pick <strong>A</strong>.
                              </Typography>
                            )}
                            {kuA && !kuB && (
                              <>
                                <Typography sx={{ fontSize: 10, color: 'text.secondary', fontWeight: 700 }}>
                                  A
                                </Typography>
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
                                  {descriptor(kuA)}
                                </Typography>
                                <Typography sx={{ fontSize: 11, color: 'text.secondary', fontStyle: 'italic' }}>
                                  Now click a second KU for <strong>B</strong>.
                                </Typography>
                              </>
                            )}
                            {kuA && kuB && (
                              <>
                                <Typography sx={{ fontSize: 10, color: 'text.secondary', fontWeight: 700 }}>
                                  A
                                </Typography>
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
                                  {descriptor(kuA)}
                                </Typography>
                                <Typography sx={{ fontSize: 10, color: 'text.secondary', fontWeight: 700 }}>
                                  B
                                </Typography>
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.primary', mb: 1 }}>
                                  {descriptor(kuB)}
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  {CROSS_REF_RELATIONS.map((meta) => (
                                    <Button
                                      key={meta.value}
                                      size="small"
                                      variant="contained"
                                      onClick={() => record(meta.value)}
                                      title={meta.description}
                                      sx={{
                                        fontSize: 11,
                                        fontWeight: 700,
                                        bgcolor: meta.color,
                                        color: 'white',
                                        justifyContent: 'flex-start',
                                        '&:hover': { bgcolor: meta.color, filter: 'brightness(0.9)' },
                                      }}
                                    >
                                      {meta.directional ? `A ${meta.label} B` : meta.label}
                                    </Button>
                                  ))}
                                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25 }}>
                                    <Button size="small" onClick={swap} sx={{ fontSize: 11, flex: 1 }}>
                                      ↔ Swap
                                    </Button>
                                    <Button
                                      size="small"
                                      onClick={clear}
                                      sx={{ fontSize: 11, flex: 1, color: 'text.secondary' }}
                                    >
                                      ✕ Cancel
                                    </Button>
                                  </Box>
                                </Box>
                              </>
                            )}
                          </Box>

                          {/* Existing relations list */}
                          <Box
                            sx={{
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1.5,
                              bgcolor: 'background.paper',
                              maxHeight: 420,
                              overflowY: 'auto',
                            }}
                          >
                            <Box
                              sx={{
                                px: 1.25,
                                py: 1,
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                bgcolor: '#fafbfc',
                                position: 'sticky',
                                top: 0,
                              }}
                            >
                              <Typography
                                sx={{ fontSize: 10, fontWeight: 800, color: 'text.secondary', letterSpacing: '0.05em' }}
                              >
                                RELATIONS ({stdLinksList.length})
                              </Typography>
                            </Box>
                            {stdLinksList.length === 0 ? (
                              <Typography sx={{ fontSize: 11, color: 'text.disabled', fontStyle: 'italic', p: 1.25 }}>
                                No relations for this standard yet.
                              </Typography>
                            ) : (
                              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                {stdLinksList.map((r) => {
                                  const a = kuById.get(r.from_ku_id)
                                  const b = kuById.get(r.to_ku_id)
                                  if (!a || !b) return null
                                  const meta = CROSS_REF_RELATIONS.find((m) => m.value === r.relation_type)
                                  const labelText = meta?.directional
                                    ? `${meta.label.replace('A ', '').replace(' B', '')}`
                                    : meta?.label || r.relation_type
                                  const isActivePair =
                                    (pickedKuId === r.from_ku_id && pickedPairKuId === r.to_ku_id) ||
                                    (pickedKuId === r.to_ku_id && pickedPairKuId === r.from_ku_id)
                                  return (
                                    <Box
                                      key={r.id}
                                      onClick={() => {
                                        // Highlight the pair. Loads A=from, B=to so the
                                        // action panel buttons can be used to re-record a
                                        // different relation type for this pair.
                                        setPickedKuId(r.from_ku_id)
                                        setPickedPairKuId(r.to_ku_id)
                                      }}
                                      sx={{
                                        p: 1,
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                        cursor: 'pointer',
                                        bgcolor: isActivePair ? '#eff6ff' : 'background.paper',
                                        '&:hover': { bgcolor: isActivePair ? '#dbeafe' : 'grey.50' },
                                      }}
                                    >
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                                        <Chip
                                          label={labelText}
                                          size="small"
                                          sx={{
                                            height: 16,
                                            fontSize: 9,
                                            fontWeight: 700,
                                            bgcolor: (meta?.color || '#666') + '22',
                                            color: meta?.color || '#666',
                                          }}
                                        />
                                        {rowHasClassMismatch(r) && (
                                          <Chip
                                            label={`⚠ ${labelOf(a)}≠${labelOf(b)}`}
                                            size="small"
                                            title="Same KU but classified differently — taxonomy-ambiguity finding"
                                            sx={{
                                              height: 16,
                                              fontSize: 9,
                                              fontWeight: 700,
                                              bgcolor: '#fef3c7',
                                              color: '#92400e',
                                            }}
                                          />
                                        )}
                                        <Box sx={{ ml: 'auto', display: 'flex', gap: 0.25 }}>
                                          <Button
                                            size="small"
                                            onClick={async (e) => {
                                              e.stopPropagation()
                                              await unlinkKUs(r.from_ku_id, r.to_ku_id, r.relation_type)
                                            }}
                                            title="Delete this relation"
                                            sx={{
                                              minWidth: 0,
                                              fontSize: 10,
                                              px: 0.75,
                                              color: '#dc2626',
                                              '&:hover': { bgcolor: '#fee2e2' },
                                            }}
                                          >
                                            ✕
                                          </Button>
                                        </Box>
                                      </Box>
                                      <Typography sx={{ fontSize: 11, color: 'text.primary', lineHeight: 1.35 }}>
                                        <strong>A:</strong> {a.name}
                                      </Typography>
                                      <Typography sx={{ fontSize: 11, color: 'text.primary', lineHeight: 1.35 }}>
                                        <strong>B:</strong> {b.name}
                                      </Typography>
                                    </Box>
                                  )
                                })}
                              </Box>
                            )}
                          </Box>
                          <Typography sx={{ fontSize: 10, color: 'text.disabled', mt: 0.5, fontStyle: 'italic' }}>
                            Tip: click a relation to highlight its KUs. To reclassify, click the relation, then pick a
                            different button above (the old one is removed separately with ✕).
                          </Typography>
                        </Box>
                      )
                    })()}

                    {/* ── Main: three-column grid (scrollable with page) ── */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5 }}>
                        {PASS_META.map((pass) => {
                          const bucket = stdBuckets ? stdBuckets[pass.id] : []
                          return (
                            <Box
                              key={pass.id}
                              sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1.5,
                                bgcolor: 'background.paper',
                                minHeight: 200,
                              }}
                            >
                              {/* Column header */}
                              <Box
                                sx={{
                                  px: 1.5,
                                  py: 1,
                                  borderBottom: '1px solid',
                                  borderColor: 'divider',
                                  bgcolor: pass.bg,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                }}
                              >
                                <Typography sx={{ fontSize: 12, fontWeight: 800, color: pass.color }}>
                                  {pass.icon} {pass.label}
                                </Typography>
                                <Typography sx={{ fontSize: 11, color: pass.color, fontWeight: 700 }}>
                                  {bucket.length} KU{bucket.length !== 1 ? 's' : ''}
                                </Typography>
                              </Box>

                              {/* KU list */}
                              <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {bucket.map((ku) => {
                                  const isFirstPicked = pickedKuId === ku.id
                                  const isSecondPicked = pickedPairKuId === ku.id
                                  const anyPicked = isFirstPicked || isSecondPicked

                                  // Any existing relation between this KU and the first
                                  // picked KU (either direction, any type). Used to show
                                  // a badge on candidate KUs.
                                  const hasAnyRelationWithPicked = pickedKuId
                                    ? crossRefs.some(
                                        (r) =>
                                          (r.from_ku_id === pickedKuId && r.to_ku_id === ku.id) ||
                                          (r.to_ku_id === pickedKuId && r.from_ku_id === ku.id),
                                      )
                                    : false

                                  // Classification for this KU (shown as leading chip)
                                  const thisType = ku.type || ku.proposed_type
                                  const unclearMeta = KU_TYPES.find((t) => t.value === 'unclear')!
                                  const typeMeta = thisType
                                    ? KU_TYPES.find((t) => t.value === thisType) || unclearMeta
                                    : unclearMeta
                                  const typeColor = typeMeta.color
                                  const typeLabel = typeMeta.label
                                  const typeIsConfirmed = !!ku.type

                                  // Total cross-pass link count for this KU (for badge)
                                  const totalLinks = crossRefs.filter(
                                    (r) => r.from_ku_id === ku.id || r.to_ku_id === ku.id,
                                  ).length

                                  const handleClick = () => {
                                    // Two-pick flow: first click sets A, second click sets B.
                                    // Clicking an already-picked KU deselects that slot.
                                    // Clicking a new KU when both slots are filled shifts
                                    // to a fresh pair (new KU becomes A, B clears).
                                    if (isFirstPicked) {
                                      setPickedKuId(pickedPairKuId)
                                      setPickedPairKuId(null)
                                      return
                                    }
                                    if (isSecondPicked) {
                                      setPickedPairKuId(null)
                                      return
                                    }
                                    if (!pickedKuId) {
                                      setPickedKuId(ku.id)
                                      return
                                    }
                                    if (!pickedPairKuId) {
                                      setPickedPairKuId(ku.id)
                                      return
                                    }
                                    // Both filled and this is a new KU — restart pair.
                                    setPickedKuId(ku.id)
                                    setPickedPairKuId(null)
                                  }

                                  return (
                                    <Box
                                      key={ku.id}
                                      onClick={handleClick}
                                      sx={{
                                        p: 1.25,
                                        border: '1.5px solid',
                                        borderColor: isFirstPicked
                                          ? pass.color
                                          : isSecondPicked
                                          ? '#16a34a'
                                          : hasAnyRelationWithPicked
                                          ? '#65a30d'
                                          : 'divider',
                                        borderLeft: `3px solid ${typeColor}`,
                                        bgcolor: isFirstPicked
                                          ? pass.bg
                                          : isSecondPicked
                                          ? '#f0fdf4'
                                          : 'background.paper',
                                        borderRadius: 1.5,
                                        cursor: 'pointer',
                                        transition: 'all 0.1s',
                                        '&:hover': {
                                          bgcolor: anyPicked ? undefined : 'grey.50',
                                        },
                                      }}
                                    >
                                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
                                        <Chip
                                          label={typeLabel}
                                          size="small"
                                          sx={{
                                            height: 18,
                                            fontSize: 10,
                                            fontWeight: 700,
                                            bgcolor: typeColor + '18',
                                            color: typeColor,
                                            border: `1px solid ${typeColor}40`,
                                            borderStyle: typeIsConfirmed ? 'solid' : 'dashed',
                                            flexShrink: 0,
                                            mt: 0.125,
                                          }}
                                        />
                                        <Typography
                                          sx={{
                                            fontSize: 12.5,
                                            fontWeight: 700,
                                            lineHeight: 1.4,
                                            flex: 1,
                                            color: 'text.primary',
                                          }}
                                        >
                                          {isFirstPicked ? 'A · ' : isSecondPicked ? 'B · ' : ''}
                                          {ku.name}
                                        </Typography>
                                        {totalLinks > 0 && (
                                          <Chip
                                            label={totalLinks}
                                            size="small"
                                            sx={{
                                              height: 16,
                                              fontSize: 9,
                                              bgcolor: pass.color + '22',
                                              color: pass.color,
                                              fontWeight: 700,
                                              flexShrink: 0,
                                            }}
                                          />
                                        )}
                                      </Box>
                                      <Typography
                                        sx={{ fontSize: 11, color: 'text.secondary', lineHeight: 1.45, mt: 0.25 }}
                                      >
                                        {ku.description}
                                      </Typography>
                                    </Box>
                                  )
                                })}
                                {bucket.length === 0 && (
                                  <Typography sx={{ fontSize: 11, color: 'text.disabled', textAlign: 'center', py: 3 }}>
                                    No {pass.label} KUs yet
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          )
                        })}
                      </Box>
                    </Box>
                  </Box>
                )}

                {/* Findings panel */}
                <Box
                  sx={{
                    mt: 1.5,
                    p: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    bgcolor: '#fafbfc',
                  }}
                >
                  <Typography
                    sx={{ fontSize: 11, fontWeight: 800, color: 'text.secondary', letterSpacing: '0.05em', mb: 0.75 }}
                  >
                    FINDINGS
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {reqKUs.length > 0 && reqAddressed < reqKUs.length && (
                      <Typography sx={{ fontSize: 12, color: '#92400e' }}>
                        ⚠ {reqKUs.length - reqAddressed} Requirement KU{reqKUs.length - reqAddressed !== 1 ? 's' : ''}{' '}
                        not addressed in Content — lesson-plan coverage gap.
                      </Typography>
                    )}
                    {contKUs.length > 0 && contGrounded < contKUs.length && (
                      <Typography sx={{ fontSize: 12, color: '#92400e' }}>
                        ⚠ {contKUs.length - contGrounded} Content KU{contKUs.length - contGrounded !== 1 ? 's' : ''} not
                        grounded in Knowledge — domain-trace gap.
                      </Typography>
                    )}
                    {orphanKnow > 0 && (
                      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                        ℹ {orphanKnow} Knowledge KU{orphanKnow !== 1 ? 's' : ''} not used in Content — adjacent domain
                        context beyond the standard.
                      </Typography>
                    )}
                    {sameAsWithClassMismatch > 0 && (
                      <Typography sx={{ fontSize: 12, color: '#92400e' }}>
                        ⚠ {sameAsWithClassMismatch} Same-KU pair{sameAsWithClassMismatch !== 1 ? 's' : ''} classified
                        differently — taxonomy ambiguity signal. These are KUs the researcher marked as identical but
                        the classifier assigned divergent types to.
                      </Typography>
                    )}
                    {mismatchLinks > 0 && mismatchLinks !== sameAsWithClassMismatch && (
                      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                        ℹ {mismatchLinks - sameAsWithClassMismatch} other linked pair
                        {mismatchLinks - sameAsWithClassMismatch !== 1 ? 's' : ''} have divergent classifications (e.g.,
                        subset/addresses across passes with different types — expected when the hierarchy generalizes).
                      </Typography>
                    )}
                    {reqKUs.length > 0 &&
                      reqAddressed === reqKUs.length &&
                      contGrounded === contKUs.length &&
                      contKUs.length > 0 && (
                        <Typography sx={{ fontSize: 12, color: '#166534' }}>
                          ✓ Full REQ ⊆ CONT ⊆ KNOW containment for this standard.
                        </Typography>
                      )}
                    {reqKUs.length === 0 && contKUs.length === 0 && knowKUs.length === 0 && (
                      <Typography sx={{ fontSize: 12, color: 'text.disabled', fontStyle: 'italic' }}>
                        This standard has no KUs in any pass. Generate some first.
                      </Typography>
                    )}
                  </Box>
                </Box>
              </>
            )
          })()}
        </Box>
      )}

      {/* ── Overview tab ── */}
      {activeTab === 'design' && (
        <Box sx={{ flex: 1, overflowY: 'auto', px: 2, pb: 4 }}>
          {/* ═══════════════════════════════════════════════════════════════
              SECTION 1: Framework & Definitions (paper body)
              ═══════════════════════════════════════════════════════════════ */}
          <Box sx={{ mt: 1.5, mb: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', mb: 0.625 }}>
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: 'text.secondary',
                    fontSize: 11,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    display: 'block',
                  }}
                >
                  Framework & Definitions
                  <Chip
                    label="paper body"
                    size="small"
                    sx={{
                      ml: 0.75,
                      height: 14,
                      fontSize: 9,
                      fontWeight: 700,
                      bgcolor: '#e8edf7',
                      color: 'primary.main',
                    }}
                  />
                  {frameworkDirty ? (
                    <Chip
                      label="● unsaved"
                      size="small"
                      sx={{ ml: 0.5, height: 14, fontSize: 9, fontWeight: 700, bgcolor: '#fff7ed', color: '#b45309' }}
                    />
                  ) : savedFramework ? (
                    <Chip
                      label="✓ saved"
                      size="small"
                      sx={{ ml: 0.5, height: 14, fontSize: 9, fontWeight: 600, bgcolor: '#dcfce7', color: '#166534' }}
                    />
                  ) : null}
                </Typography>
                <Typography sx={{ fontSize: 10.5, color: 'text.disabled', mt: 0.25 }}>
                  Prose version for the paper's body. The theoretical grounding readers need to understand the research.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <Button
                  size="small"
                  variant={frameworkDirty ? 'contained' : 'outlined'}
                  onClick={handleSaveFramework}
                  disabled={!frameworkDirty || savingFramework}
                  sx={{
                    fontSize: 11,
                    fontWeight: 800,
                    minWidth: 0,
                    px: 1.5,
                    ...(frameworkDirty
                      ? { bgcolor: '#16a34a', color: 'white', '&:hover': { bgcolor: '#15803d' } }
                      : {}),
                  }}
                >
                  {savingFramework ? <CircularProgress size={12} sx={{ mr: 0.75, color: 'inherit' }} /> : null}
                  {savingFramework ? 'Saving…' : frameworkDirty ? '💾 Save Version' : 'Saved'}
                </Button>
                {framework.trim() && (
                  <>
                    <Button
                      size="small"
                      onClick={handleReviewFramework}
                      disabled={reviewingFramework || draftingFramework || improvingFramework || optimizing}
                      sx={{ fontSize: 11, fontWeight: 700, color: 'primary.main', minWidth: 0, px: 1.25 }}
                    >
                      {reviewingFramework ? <CircularProgress size={12} sx={{ mr: 0.75 }} /> : null}⚖ Review
                    </Button>
                    <Button
                      size="small"
                      onClick={handleImproveFramework}
                      disabled={improvingFramework || draftingFramework || reviewingFramework || optimizing}
                      sx={{ fontSize: 11, fontWeight: 700, color: 'primary.main', minWidth: 0, px: 1.25 }}
                    >
                      {improvingFramework ? <CircularProgress size={12} sx={{ mr: 0.75 }} /> : null}↻ Improve
                    </Button>
                  </>
                )}
                <Button
                  size="small"
                  onClick={handleDraftFramework}
                  disabled={draftingFramework || improvingFramework || reviewingFramework || optimizing}
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: 'primary.main',
                    bgcolor: '#e8edf7',
                    minWidth: 0,
                    px: 1.25,
                  }}
                >
                  {draftingFramework ? <CircularProgress size={12} sx={{ mr: 0.75 }} /> : null}
                  {framework.trim() ? '✦ Redraft' : '✦ Draft with AI'}
                </Button>
              </Box>
            </Box>
            {frameworkError && (
              <Alert severity="error" sx={{ mb: 0.75, fontSize: 11 }} onClose={() => setFrameworkError(null)}>
                {frameworkError}
              </Alert>
            )}

            <TextField
              value={framework}
              onChange={(e) => setFramework(e.target.value)}
              placeholder="Optional. Write your theoretical framework and term definitions (KU, knowledge component, etc.) as they should appear in the paper body. Click ⚡ Optimize in the Review panel to generate the machine-optimized version below."
              fullWidth
              multiline
              minRows={14}
              maxRows={40}
              size="small"
              inputProps={{ style: { fontSize: 13, lineHeight: 1.6 } }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5, px: 0.5 }}>
              <Typography sx={{ fontSize: 10.5, color: 'text.disabled' }}>
                {framework.length.toLocaleString()} chars ·{' '}
                {framework.trim() ? framework.trim().split(/\s+/).length.toLocaleString() : 0} words
              </Typography>
              {framework.trim() && (
                <Button
                  size="small"
                  onClick={() => {
                    navigator.clipboard.writeText(framework)
                    setSaveStatus('saved')
                    setTimeout(() => setSaveStatus('idle'), 1000)
                  }}
                  sx={{ fontSize: 10.5, fontWeight: 700, minWidth: 0, px: 1 }}
                >
                  📋 Copy for paper body
                </Button>
              )}
            </Box>

            {/* Review panel */}
            {frameworkReview && (
              <Box
                sx={{
                  mt: 1,
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  bgcolor: '#fafbfc',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 12, color: 'primary.main' }}>
                    ⚖ Framework Review
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Button
                      size="small"
                      onClick={handleOptimizeFramework}
                      disabled={optimizing}
                      sx={{ fontSize: 10.5, fontWeight: 700, color: 'primary.main', px: 1 }}
                    >
                      {optimizing ? <CircularProgress size={10} sx={{ mr: 0.5 }} /> : null}⚡ Optimize for Extraction →
                    </Button>
                    <Button
                      size="small"
                      onClick={() => setFrameworkReview(null)}
                      sx={{ fontSize: 10.5, color: 'text.secondary' }}
                    >
                      Dismiss
                    </Button>
                  </Box>
                </Box>

                {frameworkReview.overall && (
                  <Typography sx={{ fontSize: 11.5, color: 'text.primary', mb: 1.25, fontStyle: 'italic' }}>
                    {frameworkReview.overall}
                  </Typography>
                )}

                {frameworkReview.strengths && frameworkReview.strengths.length > 0 && (
                  <Box sx={{ mb: 1 }}>
                    <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#16a34a', mb: 0.25 }}>
                      ✓ Strengths
                    </Typography>
                    {frameworkReview.strengths.map((s, i) => (
                      <Typography key={i} sx={{ fontSize: 11, color: 'text.secondary', ml: 1.5, lineHeight: 1.5 }}>
                        • {s}
                      </Typography>
                    ))}
                  </Box>
                )}

                {frameworkReview.gaps && frameworkReview.gaps.length > 0 && (
                  <Box sx={{ mb: 1 }}>
                    <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#d97706', mb: 0.25 }}>⚠ Gaps</Typography>
                    {frameworkReview.gaps.map((s, i) => (
                      <Typography key={i} sx={{ fontSize: 11, color: 'text.secondary', ml: 1.5, lineHeight: 1.5 }}>
                        • {s}
                      </Typography>
                    ))}
                  </Box>
                )}

                {frameworkReview.ambiguities && frameworkReview.ambiguities.length > 0 && (
                  <Box sx={{ mb: 1 }}>
                    <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#7c3aed', mb: 0.25 }}>
                      ? Ambiguities
                    </Typography>
                    {frameworkReview.ambiguities.map((s, i) => (
                      <Typography key={i} sx={{ fontSize: 11, color: 'text.secondary', ml: 1.5, lineHeight: 1.5 }}>
                        • {s}
                      </Typography>
                    ))}
                  </Box>
                )}

                {frameworkReview.suggestions && frameworkReview.suggestions.length > 0 && (
                  <Box>
                    <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#0369a1', mb: 0.25 }}>
                      → Suggestions
                    </Typography>
                    {frameworkReview.suggestions.map((s, i) => (
                      <Typography key={i} sx={{ fontSize: 11, color: 'text.secondary', ml: 1.5, lineHeight: 1.5 }}>
                        • {s}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </Box>

          {/* ═══════════════════════════════════════════════════════════════
              SECTION 2: AI-Optimized System Prompt (paper appendix + LLM)
              ═══════════════════════════════════════════════════════════════ */}
          <Box sx={{ mt: 1.5, mb: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', mb: 0.625 }}>
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: 'text.secondary',
                    fontSize: 11,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    display: 'block',
                  }}
                >
                  System Prompt (AI-Optimized)
                  <Chip
                    label="appendix · LLM input"
                    size="small"
                    sx={{ ml: 0.75, height: 14, fontSize: 9, fontWeight: 700, bgcolor: '#fef3c7', color: '#92400e' }}
                  />
                  {systemPromptDirty ? (
                    <Chip
                      label="● unsaved"
                      size="small"
                      sx={{ ml: 0.5, height: 14, fontSize: 9, fontWeight: 700, bgcolor: '#fff7ed', color: '#b45309' }}
                    />
                  ) : savedSystemPrompt ? (
                    <Chip
                      label="✓ saved"
                      size="small"
                      sx={{ ml: 0.5, height: 14, fontSize: 9, fontWeight: 600, bgcolor: '#dcfce7', color: '#166534' }}
                    />
                  ) : null}
                </Typography>
                <Typography sx={{ fontSize: 10.5, color: 'text.disabled', mt: 0.25 }}>
                  Machine-formatted version for the paper appendix. This text is sent as the system prompt on every run
                  (cached by the provider).
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <Button
                  size="small"
                  variant={systemPromptDirty ? 'contained' : 'outlined'}
                  onClick={handleSaveSystemPrompt}
                  disabled={!systemPromptDirty || savingSystemPrompt}
                  sx={{
                    fontSize: 11,
                    fontWeight: 800,
                    minWidth: 0,
                    px: 1.5,
                    ...(systemPromptDirty
                      ? { bgcolor: '#16a34a', color: 'white', '&:hover': { bgcolor: '#15803d' } }
                      : {}),
                  }}
                >
                  {savingSystemPrompt ? <CircularProgress size={12} sx={{ mr: 0.75, color: 'inherit' }} /> : null}
                  {savingSystemPrompt ? 'Saving…' : systemPromptDirty ? '💾 Save Version' : 'Saved'}
                </Button>
                {optimizedPrompt.trim() && (
                  <>
                    <Button
                      size="small"
                      onClick={handleReviewOptimized}
                      disabled={reviewingOptimized || improvingOptimized || optimizing}
                      sx={{ fontSize: 11, fontWeight: 700, color: '#92400e', minWidth: 0, px: 1.25 }}
                    >
                      {reviewingOptimized ? <CircularProgress size={12} sx={{ mr: 0.75 }} /> : null}⚖ Review
                    </Button>
                    <Button
                      size="small"
                      onClick={handleImproveOptimized}
                      disabled={improvingOptimized || reviewingOptimized || optimizing}
                      sx={{ fontSize: 11, fontWeight: 700, color: '#92400e', minWidth: 0, px: 1.25 }}
                    >
                      {improvingOptimized ? <CircularProgress size={12} sx={{ mr: 0.75 }} /> : null}↻ Improve
                    </Button>
                  </>
                )}
                {/* Optimize from Framework → writes to this section */}
                <Button
                  size="small"
                  onClick={handleOptimizeFramework}
                  disabled={optimizing || reviewingOptimized || improvingOptimized || !framework.trim()}
                  title={
                    framework.trim()
                      ? 'Rewrite Framework & Definitions for machine parsing, output here'
                      : 'Write a Framework & Definitions above first'
                  }
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#92400e',
                    bgcolor: '#fef3c7',
                    minWidth: 0,
                    px: 1.25,
                    '&:hover': { bgcolor: '#fde68a' },
                    '&.Mui-disabled': { bgcolor: '#fef3c780', color: '#92400e60' },
                  }}
                >
                  {optimizing ? <CircularProgress size={12} sx={{ mr: 0.75 }} /> : null}
                  {optimizedPrompt.trim() ? '⚡ Re-optimize from Framework' : '⚡ Optimize from Framework'}
                </Button>
              </Box>
            </Box>
            {optimizedError && (
              <Alert severity="error" sx={{ mb: 0.75, fontSize: 11 }} onClose={() => setOptimizedError(null)}>
                {optimizedError}
              </Alert>
            )}

            {/* Undo banner after optimize (TODO: upgrade to inline diff view) */}
            {preOptimizeSnapshot !== null && (
              <Alert
                severity="info"
                sx={{ mb: 0.75, fontSize: 11, alignItems: 'center' }}
                action={
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Button size="small" onClick={undoOptimize} sx={{ fontSize: 10.5, fontWeight: 700 }}>
                      ↶ Undo
                    </Button>
                    <Button
                      size="small"
                      onClick={() => setPreOptimizeSnapshot(null)}
                      sx={{ fontSize: 10.5, fontWeight: 700 }}
                    >
                      Keep
                    </Button>
                  </Box>
                }
              >
                AI restructured your framework for machine parsing. Review and keep or undo.
              </Alert>
            )}

            <TextField
              value={optimizedPrompt}
              onChange={(e) => {
                setOptimizedPrompt(e.target.value)
                if (preOptimizeSnapshot !== null) setPreOptimizeSnapshot(null)
              }}
              placeholder="This field is populated by ⚡ Optimize for Extraction. You can edit directly if you prefer — changes are sent to the LLM on the next run."
              fullWidth
              multiline
              minRows={14}
              maxRows={40}
              size="small"
              inputProps={{ style: { fontSize: 12, fontFamily: "'Courier New', monospace", lineHeight: 1.55 } }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5, px: 0.5 }}>
              <Typography sx={{ fontSize: 10.5, color: 'text.disabled' }}>
                {optimizedPrompt.length.toLocaleString()} chars ·{' '}
                {optimizedPrompt.trim() ? optimizedPrompt.trim().split(/\s+/).length.toLocaleString() : 0} words
              </Typography>
              {optimizedPrompt.trim() && (
                <Button
                  size="small"
                  onClick={() => {
                    navigator.clipboard.writeText(optimizedPrompt)
                    setSaveStatus('saved')
                    setTimeout(() => setSaveStatus('idle'), 1000)
                  }}
                  sx={{ fontSize: 10.5, fontWeight: 700, minWidth: 0, px: 1 }}
                >
                  📋 Copy for appendix
                </Button>
              )}
            </Box>

            {/* Review panel for the Optimized System Prompt */}
            {optimizedReview && (
              <Box
                sx={{
                  mt: 1,
                  p: 1.5,
                  border: '1px solid',
                  borderColor: '#fde68a',
                  borderRadius: 1.5,
                  bgcolor: '#fffbeb',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 12, color: '#92400e' }}>
                    ⚖ System Prompt Review
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => setOptimizedReview(null)}
                    sx={{ fontSize: 10.5, color: 'text.secondary' }}
                  >
                    Dismiss
                  </Button>
                </Box>

                {optimizedReview.overall && (
                  <Typography sx={{ fontSize: 11.5, color: 'text.primary', mb: 1.25, fontStyle: 'italic' }}>
                    {optimizedReview.overall}
                  </Typography>
                )}

                {optimizedReview.strengths && optimizedReview.strengths.length > 0 && (
                  <Box sx={{ mb: 1 }}>
                    <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#16a34a', mb: 0.25 }}>
                      ✓ Strengths
                    </Typography>
                    {optimizedReview.strengths.map((s, i) => (
                      <Typography key={i} sx={{ fontSize: 11, color: 'text.secondary', ml: 1.5, lineHeight: 1.5 }}>
                        • {s}
                      </Typography>
                    ))}
                  </Box>
                )}

                {optimizedReview.gaps && optimizedReview.gaps.length > 0 && (
                  <Box sx={{ mb: 1 }}>
                    <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#d97706', mb: 0.25 }}>⚠ Gaps</Typography>
                    {optimizedReview.gaps.map((s, i) => (
                      <Typography key={i} sx={{ fontSize: 11, color: 'text.secondary', ml: 1.5, lineHeight: 1.5 }}>
                        • {s}
                      </Typography>
                    ))}
                  </Box>
                )}

                {optimizedReview.ambiguities && optimizedReview.ambiguities.length > 0 && (
                  <Box sx={{ mb: 1 }}>
                    <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#7c3aed', mb: 0.25 }}>
                      ? Ambiguities
                    </Typography>
                    {optimizedReview.ambiguities.map((s, i) => (
                      <Typography key={i} sx={{ fontSize: 11, color: 'text.secondary', ml: 1.5, lineHeight: 1.5 }}>
                        • {s}
                      </Typography>
                    ))}
                  </Box>
                )}

                {optimizedReview.suggestions && optimizedReview.suggestions.length > 0 && (
                  <Box>
                    <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#0369a1', mb: 0.25 }}>
                      → Suggestions
                    </Typography>
                    {optimizedReview.suggestions.map((s, i) => (
                      <Typography key={i} sx={{ fontSize: 11, color: 'text.secondary', ml: 1.5, lineHeight: 1.5 }}>
                        • {s}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            <Typography
              variant="caption"
              sx={{ display: 'block', mt: 0.75, fontSize: 10.5, color: 'text.secondary', lineHeight: 1.5 }}
            >
              Every run snapshots both the Framework and the System Prompt active at execution time, so historical runs
              retain their original context even if you edit later.
            </Typography>
          </Box>

          {/* Dataset description — AI drafted, researcher editable */}
          <Box sx={{ mt: 1.5, mb: 1.75 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.625 }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: 'text.secondary',
                  fontSize: 11,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                }}
              >
                Dataset Description
              </Typography>
              <Button
                size="small"
                onClick={handleDraftDescription}
                disabled={drafting}
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: 'primary.main',
                  bgcolor: '#e8edf7',
                  minWidth: 0,
                  px: 1.5,
                  py: 0.25,
                }}
              >
                {drafting ? <CircularProgress size={12} sx={{ mr: 0.75 }} /> : null}
                {drafting ? 'Drafting…' : description ? 'Redraft' : '✦ Draft with AI'}
              </Button>
            </Box>
            {draftError && (
              <Alert severity="error" sx={{ mb: 0.75, fontSize: 11 }} onClose={() => setDraftError(null)}>
                {draftError}
              </Alert>
            )}
            <TextField
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="A paragraph describing this dataset as it would appear in a research report. Use '✦ Draft with AI' to generate a starting point."
              fullWidth
              multiline
              minRows={8}
              maxRows={30}
              size="small"
              inputProps={{ style: { fontSize: 13 } }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5, px: 0.5 }}>
              <Typography sx={{ fontSize: 10.5, color: 'text.disabled' }}>
                {description.length.toLocaleString()} chars ·{' '}
                {description.trim() ? description.trim().split(/\s+/).length.toLocaleString() : 0} words
              </Typography>
              {description.trim() && (
                <Button
                  size="small"
                  onClick={() => {
                    navigator.clipboard.writeText(description)
                    setSaveStatus('saved')
                    setTimeout(() => setSaveStatus('idle'), 1000)
                  }}
                  sx={{ fontSize: 10.5, fontWeight: 700, minWidth: 0, px: 1 }}
                >
                  📋 Copy
                </Button>
              )}
            </Box>
            <Typography
              variant="caption"
              sx={{ display: 'block', mt: 0.75, fontSize: 10.5, color: 'text.secondary', lineHeight: 1.5 }}
            >
              This text is AI-generated from your dataset metadata and must be reviewed, revised, and verified by the
              researcher before inclusion in any publication. Use of AI-assisted text without disclosure and substantive
              revision may constitute a breach of research integrity standards. The researcher bears full responsibility
              for the accuracy of any published description.
            </Typography>
          </Box>

          {/* Characterization panel — statistics that informed the description */}
          <DatasetCharacterizationPanel
            standardIds={stdIds}
            findStandard={findStandard}
            subjectColor={sm.color}
            subjectBg={sm.bg}
            collapsible
            defaultOpen
          />

          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ display: 'block', textAlign: 'center', mt: 2, fontSize: 10 }}
          >
            Created {new Date(experiment.created_at).toLocaleDateString()}
          </Typography>
        </Box>
      )}

      {/* ── Next-step CTA footer ── */}
      {(() => {
        // Gating removed temporarily so researcher can move freely between
        // tabs during testing. Re-add readiness checks after the data-
        // collection pipeline is stable.
        let nextLabel = ''
        let nextAction: (() => void) | null = null
        const hint = ''
        if (activeTab === 'design') {
          nextLabel = 'Next: Set up Conditions →'
          nextAction = () => setActiveTab('conditions')
        } else if (activeTab === 'conditions') {
          nextLabel = 'Next: Generate KUs on Dataset →'
          nextAction = () => setActiveTab('standards')
        } else if (activeTab === 'standards') {
          nextLabel = 'Next: Analysis →'
          nextAction = () => setActiveTab('analysis')
        } else if (activeTab === 'analysis') {
          nextLabel = 'Next: Expert Review →'
          nextAction = () => navigate(`/experiments/${experiment.id}/review`)
        }
        if (!nextLabel && !hint) return null
        return (
          <Box
            sx={{
              flexShrink: 0,
              px: 2,
              py: 1,
              borderTop: '1px solid',
              borderColor: 'divider',
              bgcolor: '#fafbfc',
              display: 'flex',
              alignItems: 'center',
              justifyContent: nextLabel ? 'flex-end' : 'center',
              gap: 1,
            }}
          >
            {hint && (
              <Typography sx={{ fontSize: 11, color: 'text.secondary', fontStyle: 'italic' }}>{hint}</Typography>
            )}
            {nextLabel && nextAction && (
              <Button
                variant="contained"
                size="small"
                onClick={nextAction}
                sx={{
                  bgcolor: sm.color,
                  '&:hover': { bgcolor: sm.color + 'dd' },
                  fontSize: 11,
                  fontWeight: 700,
                  px: 2,
                }}
              >
                {nextLabel}
              </Button>
            )}
          </Box>
        )
      })()}
    </Box>
  )
}
